import PropTypes from "prop-types";
import {
  FaTrash,
  FaCrop,
  FaCopy,
  FaCut,
  FaUndo,
  FaRedo,
  FaSync,
} from "react-icons/fa";
import "../styles.css";
import { useRef, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { convertImageToBase64 } from "../../../SectionElements/plugins/utils";
import AdvancedCropModal from "../crop/AdvancedCropModal";
import imageCompression from "browser-image-compression";
import { convertSvgToJpeg } from "../../utils/mediaInteractionHandlers";
import {
  IMAGE_PRESETS_CHANGED_EVENT,
  IMAGE_PRESETS_STORAGE_KEY,
  loadImagePresets,
  persistImagePresets,
} from "../../utils/imagePresetsStorage";
import {
  getCanvasStageHeightPx,
  getMainCanvasWidthPx,
  getOffCanvasStageWidthPx,
  getStageHeightPxFromCanvasHeight,
} from "../../utils/mainCanvasLayout";
import { getOffCanvasMainColumnBounds } from "../../utils/boundaryUtils";
import CopyIcon from "../selection/CopyIcon";

const TYPING_INPUT_TYPES = new Set([
  "insertText",
  "deleteContentBackward",
  "deleteContentForward",
  "insertCompositionText",
  "insertFromPaste",
  "insertFromDrop",
  "deleteByCut",
]);

const shouldDeferInputUpdate = (inputType) =>
  inputType && TYPING_INPUT_TYPES.has(inputType);

const IMAGE_DEFAULT_PRESET_STORAGE_KEY = "imageDefaultStylePreset";
const IMAGE_DEFAULT_PRESET_ID_STORAGE_KEY = "imageDefaultStylePresetId";

const formatNumericInputValue = (value) => {
  if (!Number.isFinite(value)) return "0";
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
};

// Helper function to convert negative x/y values to 0 for display only
const getDisplayValue = (value) => {
  if (value === "" || value === null || value === undefined) return value;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num < 0 ? "0" : value;
};

const groupStyle = {
  display: "flex",
  gap: "8px",
  padding: "8px",
  background: "#F7F7F7", // greyish background
  borderRadius: "4px",
};

function ImageEditor({
  selectedImage,
  updateImageProperties,
  onDelete = () => { },
  onCopy = () => { },
  onCut = () => { },
  toggleImageCompression = () => { },
  setImages,
  canvasHeight,
  setSelectedObject = null,
  setSelectedObjects = null,
  isCropModalOpen = false,
  setIsCropModalOpen = () => { },
  setCropModalOpenRefOnly = () => { },
  setCropModalRootRef = () => { },
  setIsImageLoading,
  setImageLoadingProgress,
  saveToHistory,
  latestUpdatedImagesRef = null,
  latestImageEditorRefUrlRef = null,
  setPendingChanges = null,
  pendingChangesRef = null,
  transformerRef,
  onToggleCloneMode = null,
  isCloneMode = false,
}) {
  const EDITOR_EDGE_BUFFER_PX = 10;
  const MIN_WIDTH = 10;
  const mainStageW = Math.max(MIN_WIDTH, getMainCanvasWidthPx());
  const canvasPixelHeight =
    Number(canvasHeight) > 100
      ? Number(canvasHeight)
      : getStageHeightPxFromCanvasHeight(canvasHeight);
  const offCanvasStageHeight = getCanvasStageHeightPx();
  const canvasPixelWidth = mainStageW;
  const MAX_WIDTH = canvasPixelWidth;
  const MIN_HEIGHT = 0;
  const MAX_HEIGHT = canvasPixelHeight;
  // Ensure selectedImage.width and selectedImage.height have valid numbers,
  // using 0 as a safe fallback if they are undefined or non-numeric.
  const currentImageWidth = selectedImage?.width || 0;
  const currentImageHeight = selectedImage?.height || 0;

  // Determine if image is on off-canvas (excluding top-layer images which are on main canvas)
  // CRITICAL FIX: Check _isInOffCanvasDuringDrag flag first (for elements being dragged between canvases)
  // If flag is explicitly false, element is on main canvas (don't check x position)
  // If flag is true, element is on off-canvas
  // If flag is undefined, fall back to x position check for elements already on off-canvas
  const isOffCanvas =
    selectedImage?._previewTargetStage === "offCanvas"
      ? true
      : selectedImage?._previewTargetStage === "main"
        ? false
        : (selectedImage?._isDragging === true &&
            selectedImage?.isOffCanvas === true) ||
          (selectedImage?._isDragging === true &&
            selectedImage?._previewTargetStage !== "main" &&
            (selectedImage?.isOffCanvas === true ||
              selectedImage?._isInOffCanvasDuringDrag === true ||
              (selectedImage?._isInOffCanvasDuringDrag === undefined &&
                !selectedImage?.isOnTopLayer &&
                (selectedImage?.x < 0 ||
                  (selectedImage?.x !== undefined &&
                    selectedImage?.x >= window.innerWidth * 0.45))))) ||
          (selectedImage?._isDragging !== true &&
            (selectedImage?.isOffCanvas === true ||
              selectedImage?._isInOffCanvasDuringDrag === true ||
              (selectedImage?._isInOffCanvasDuringDrag === undefined &&
                !selectedImage?.isOnTopLayer &&
                (selectedImage?.x < 0 ||
                  (selectedImage?.x !== undefined &&
                    selectedImage?.x >= window.innerWidth * 0.45)))));

  // Calculate proper constraints based on canvas type
  const getStageConstraints = () => {
    const edgeInset = EDITOR_EDGE_BUFFER_PX;
    if (isOffCanvas) {
      // Off-canvas stage uses full viewport width
      const stageWidth = getOffCanvasStageWidthPx();
      const usableWidth = getOffCanvasMainColumnBounds(stageWidth).mainZoneStart;
      const stageHeight = offCanvasStageHeight;
      // For images, x and y are center coordinates
      const halfWidth = currentImageWidth / 2;
      const halfHeight = currentImageHeight / 2;
      return {
        minX: halfWidth + edgeInset,
        maxX: Math.max(
          halfWidth + edgeInset,
          usableWidth - halfWidth - edgeInset,
        ),
        minY: halfHeight + edgeInset,
        maxY: Math.max(
          halfHeight + edgeInset,
          stageHeight - halfHeight - edgeInset,
        ),
      };
    } else {
      // Main canvas constraints
      return {
        minX: currentImageWidth / 2 + edgeInset,
        maxX: Math.max(
          currentImageWidth / 2 + edgeInset,
          canvasPixelWidth - currentImageWidth / 2 - edgeInset,
        ),
        minY: currentImageHeight / 2 + edgeInset,
        maxY: Math.max(
          currentImageHeight / 2 + edgeInset,
          canvasPixelHeight - currentImageHeight / 2 - edgeInset,
        ),
      };
    }
  };

  const stageConstraints = getStageConstraints();
  const MIN_X_CONSTRAINED = stageConstraints.minX;
  const MAX_X_CONSTRAINED = stageConstraints.maxX;
  const MIN_Y_CONSTRAINED = stageConstraints.minY;
  const MAX_Y_CONSTRAINED = stageConstraints.maxY;
  const activeStageWidth = isOffCanvas
    ? getOffCanvasStageWidthPx()
    : canvasPixelWidth;
  const usableStageWidth = isOffCanvas
    ? getOffCanvasMainColumnBounds(activeStageWidth).mainZoneStart
    : activeStageWidth;
  const activeStageHeight = isOffCanvas
    ? offCanvasStageHeight
    : canvasPixelHeight;
  const centerX = Number(selectedImage?.x) || 0;
  const centerY = Number(selectedImage?.y) || 0;
  const maxHalfWidthByEdge = Math.min(
    centerX - EDITOR_EDGE_BUFFER_PX,
    usableStageWidth - EDITOR_EDGE_BUFFER_PX - centerX,
  );
  const maxHalfHeightByEdge = Math.min(
    centerY - EDITOR_EDGE_BUFFER_PX,
    activeStageHeight - EDITOR_EDGE_BUFFER_PX - centerY,
  );
  const dynamicMaxWidth = Math.max(
    MIN_WIDTH,
    maxHalfWidthByEdge * 2,
    Number(selectedImage?.width) || MIN_WIDTH,
  );
  const dynamicMaxHeight = Math.max(
    MIN_HEIGHT,
    maxHalfHeightByEdge * 2,
  );
  const getRotationAwareMaxWidthByStageFit = (heightForCalc) => {
    const h = Math.max(MIN_HEIGHT, Number(heightForCalc) || MIN_HEIGHT);
    const rotationDeg = Number(selectedImage?.rotation) || 0;
    const theta = (rotationDeg * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(theta));
    const absSin = Math.abs(Math.sin(theta));
    const availableProjectedW = Math.max(
      MIN_WIDTH,
      usableStageWidth - EDITOR_EDGE_BUFFER_PX * 2,
    );
    const availableProjectedH = Math.max(
      MIN_HEIGHT,
      activeStageHeight - EDITOR_EDGE_BUFFER_PX * 2,
    );
    const EPS = 1e-6;
    const maxWFromHoriz =
      absCos > EPS
        ? (availableProjectedW - h * absSin) / absCos
        : Number.POSITIVE_INFINITY;
    const maxWFromVert =
      absSin > EPS
        ? (availableProjectedH - h * absCos) / absSin
        : Number.POSITIVE_INFINITY;
    return Math.max(MIN_WIDTH, Math.min(maxWFromHoriz, maxWFromVert));
  };
  const getRotationAwareMaxHeightByStageFit = (widthForCalc) => {
    const w = Math.max(MIN_WIDTH, Number(widthForCalc) || MIN_WIDTH);
    const rotationDeg = Number(selectedImage?.rotation) || 0;
    const theta = (rotationDeg * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(theta));
    const absSin = Math.abs(Math.sin(theta));
    const availableProjectedW = Math.max(
      MIN_WIDTH,
      usableStageWidth - EDITOR_EDGE_BUFFER_PX * 2,
    );
    const availableProjectedH = Math.max(
      MIN_HEIGHT,
      activeStageHeight - EDITOR_EDGE_BUFFER_PX * 2,
    );
    const EPS = 1e-6;
    const maxHFromHoriz =
      absSin > EPS
        ? (availableProjectedW - w * absCos) / absSin
        : Number.POSITIVE_INFINITY;
    const maxHFromVert =
      absCos > EPS
        ? (availableProjectedH - w * absSin) / absCos
        : Number.POSITIVE_INFINITY;
    return Math.max(MIN_HEIGHT, Math.min(maxHFromHoriz, maxHFromVert));
  };
  // Stage-fit maxima independent of current center. Used for non-aspect-ratio edits,
  // where we should keep requested size as large as possible and reposition center.
  const maxWidthByStageFit = Math.max(
    MIN_WIDTH,
    getRotationAwareMaxWidthByStageFit(Number(selectedImage?.height) || MIN_HEIGHT),
    Number(selectedImage?.width) || MIN_WIDTH,
  );
  const maxHeightByStageFit = Math.max(
    MIN_HEIGHT,
    getRotationAwareMaxHeightByStageFit(
      Number(selectedImage?.width) || MIN_WIDTH,
    ),
  );
  const dynamicMaxHeightForInput = Math.max(
    MIN_HEIGHT,
    Math.ceil(dynamicMaxHeight - 1e-6),
  );
  const maxHeightByStageFitForInput = Math.max(
    MIN_HEIGHT,
    Math.ceil(maxHeightByStageFit - 1e-6),
  );

  // Correctly calculated limits (for backward compatibility)
  const MIN_X = currentImageWidth / 2;
  const MAX_X = canvasPixelWidth - currentImageWidth / 2;

  const MIN_Y = currentImageHeight / 2;
  const MAX_Y = canvasPixelHeight - currentImageHeight / 2;

  const fileInputRef = useRef();
  const {
    x = 0,
    y = 0,
    width = 100,
    height = 100,
    referenceUrl = "",
    rotation = 0,
    compress = true, // Default to true for compression
    maintainAspectRatio = false,
    isOnTopLayer = false,
  } = selectedImage;
  const [imagePresets, setImagePresets] = useState([]);
  const [isImagePresetsExpanded, setIsImagePresetsExpanded] = useState(true);
  const [editingImagePreset, setEditingImagePreset] = useState(null);
  const [pendingNewImagePresetId, setPendingNewImagePresetId] = useState(null);
  const [defaultImagePresetId, setDefaultImagePresetId] = useState(() =>
    localStorage.getItem(IMAGE_DEFAULT_PRESET_ID_STORAGE_KEY),
  );
  const [isEditingRotation, setIsEditingRotation] = useState(false);
  const rotationIntervalRef = useRef(null);
  const rotationStopHandlerRef = useRef(null);
  const rotationDraftRef = useRef(null);
  const [isCropLoading, setIsCropLoading] = useState(false);
  const [isReplacingImage, setIsReplacingImage] = useState(false);
  // Track the last position we displayed (for reference, not for blocking updates)
  const lastDisplayedPositionRef = useRef({
    x: selectedImage?.x,
    y: selectedImage?.y,
  });
  const pendingManualInputsRef = useRef({
    x: null,
    y: null,
    width: null,
    height: null,
    rotation: null,
  });
  const skipNextWidthBlurCommitRef = useRef(false);
  const skipNextHeightBlurCommitRef = useRef(false);
  // Track the fixed center point for rotation (for useTopLeftAnchor images)
  const fixedCenterRef = useRef(null);

  // Helper functions for rotation arrow holding
  const normalizeRotationValue = (value) => {
    let numeric =
      typeof value === "number"
        ? value
        : value === "" || value === null || value === undefined
          ? 0
          : parseInt(value, 10);
    if (Number.isNaN(numeric)) {
      numeric = 0;
    }
    let normalizedValue = numeric % 360;
    if (normalizedValue < 0) {
      normalizedValue += 360;
    }
    return normalizedValue;
  };

  const clampImagePositionForRotation = (
    rotationDeg,
    candidateX,
    candidateY,
    useTopLeftAnchor,
    widthOverride = null,
    heightOverride = null,
  ) => {
    const safeWidth = Math.max(
      0,
      Number(
        widthOverride !== null && widthOverride !== undefined
          ? widthOverride
          : selectedImage?.width,
      ) || 0,
    );
    const safeHeight = Math.max(
      0,
      Number(
        heightOverride !== null && heightOverride !== undefined
          ? heightOverride
          : selectedImage?.height,
      ) || 0,
    );
    const halfWidth = safeWidth / 2;
    const halfHeight = safeHeight / 2;
    const theta = (rotationDeg * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(theta));
    const absSin = Math.abs(Math.sin(theta));
    const rotatedHalfWidth = halfWidth * absCos + halfHeight * absSin;
    const rotatedHalfHeight = halfWidth * absSin + halfHeight * absCos;
    const minCenterX = rotatedHalfWidth + EDITOR_EDGE_BUFFER_PX;
    const maxCenterX = Math.max(
      minCenterX,
      usableStageWidth - rotatedHalfWidth - EDITOR_EDGE_BUFFER_PX,
    );
    const minCenterY = rotatedHalfHeight + EDITOR_EDGE_BUFFER_PX;
    const maxCenterY = Math.max(
      minCenterY,
      activeStageHeight - rotatedHalfHeight - EDITOR_EDGE_BUFFER_PX,
    );

    if (!useTopLeftAnchor) {
      return {
        x: Math.max(minCenterX, Math.min(maxCenterX, Number(candidateX) || 0)),
        y: Math.max(minCenterY, Math.min(maxCenterY, Number(candidateY) || 0)),
      };
    }

    const relX = -halfWidth;
    const relY = -halfHeight;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    const offsetX = relX * cos - relY * sin;
    const offsetY = relX * sin + relY * cos;
    const candidateCenterX = (Number(candidateX) || 0) - offsetX;
    const candidateCenterY = (Number(candidateY) || 0) - offsetY;
    const clampedCenterX = Math.max(
      minCenterX,
      Math.min(maxCenterX, candidateCenterX),
    );
    const clampedCenterY = Math.max(
      minCenterY,
      Math.min(maxCenterY, candidateCenterY),
    );

    return {
      x: clampedCenterX + offsetX,
      y: clampedCenterY + offsetY,
    };
  };

  const doesRotationFitAtPosition = (
    rotationDeg,
    candidateX,
    candidateY,
    useTopLeftAnchor,
  ) => {
    const clamped = clampImagePositionForRotation(
      rotationDeg,
      candidateX,
      candidateY,
      useTopLeftAnchor,
    );
    const epsilon = 0.5;
    return (
      Math.abs((Number(candidateX) || 0) - (Number(clamped.x) || 0)) <= epsilon &&
      Math.abs((Number(candidateY) || 0) - (Number(clamped.y) || 0)) <= epsilon
    );
  };
  const canRotationFitCurrentImageSize = (
    rotationDeg,
    widthOverride = null,
    heightOverride = null,
  ) => {
    const safeWidth = Math.max(
      0,
      Number(
        widthOverride !== null && widthOverride !== undefined
          ? widthOverride
          : selectedImage?.width,
      ) || 0,
    );
    const safeHeight = Math.max(
      0,
      Number(
        heightOverride !== null && heightOverride !== undefined
          ? heightOverride
          : selectedImage?.height,
      ) || 0,
    );
    const halfWidth = safeWidth / 2;
    const halfHeight = safeHeight / 2;
    const theta = (rotationDeg * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(theta));
    const absSin = Math.abs(Math.sin(theta));
    const rotatedHalfWidth = halfWidth * absCos + halfHeight * absSin;
    const rotatedHalfHeight = halfWidth * absSin + halfHeight * absCos;
    const projectedWidth = rotatedHalfWidth * 2;
    const projectedHeight = rotatedHalfHeight * 2;
    const maxProjectedWidth = Math.max(
      1,
      usableStageWidth - EDITOR_EDGE_BUFFER_PX * 2,
    );
    const maxProjectedHeight = Math.max(
      1,
      activeStageHeight - EDITOR_EDGE_BUFFER_PX * 2,
    );
    return (
      projectedWidth <= maxProjectedWidth + 0.01 &&
      projectedHeight <= maxProjectedHeight + 0.01
    );
  };

  const finalizeRotationChange = (valueOverride = null) => {
    const sourceValue =
      valueOverride !== null && valueOverride !== undefined
        ? valueOverride
        : rotationDraftRef.current !== null
          ? rotationDraftRef.current
          : localInputValues.rotation;

    const normalizedValue = normalizeRotationValue(sourceValue);
    rotationDraftRef.current = null;

    setLocalInputValues((prev) => ({
      ...prev,
      rotation: normalizedValue,
    }));
    pendingManualInputsRef.current.rotation = normalizedValue;

    // CRITICAL: Pass skipHistorySave=true to prevent saving to history on each rotation change
    // History will be saved when element is unselected via element_edit_complete
    // This groups all rotation changes (from arrow clicks) into a single history entry
    // Similar to how RectangleEditor handles rotation changes
    // CRITICAL FIX: For images with useTopLeftAnchor, we need to preserve the center point during rotation
    // not the top-left corner. This ensures rotation happens around the center, just like the transformer handle.
    const useTopLeftAnchor = selectedImage.useTopLeftAnchor === true;
    let newX = selectedImage.x;
    let newY = selectedImage.y;

    if (useTopLeftAnchor) {
      // CRITICAL FIX: Calculate or reuse the fixed center point
      // On first rotation, calculate and store the center point
      // On subsequent rotations, reuse the stored center point to prevent drift
      const halfWidth = selectedImage.width / 2;
      const halfHeight = selectedImage.height / 2;
      const currentRotation = (selectedImage.rotation || 0) * (Math.PI / 180);

      let centerX, centerY;

      // If we don't have a stored center point, or if the image dimensions changed, calculate it
      if (
        !fixedCenterRef.current ||
        fixedCenterRef.current.width !== selectedImage.width ||
        fixedCenterRef.current.height !== selectedImage.height
      ) {
        // Calculate the true center point by "unrotating" the current position
        // The top-left corner relative to center in local (unrotated) coordinates
        const relX = -halfWidth;
        const relY = -halfHeight;

        // Rotate this relative vector by the current rotation to get the offset from top-left to center
        const cosCurrent = Math.cos(currentRotation);
        const sinCurrent = Math.sin(currentRotation);
        const offsetX = relX * cosCurrent - relY * sinCurrent;
        const offsetY = relX * sinCurrent + relY * cosCurrent;

        // Calculate the true center point in world coordinates
        // Center = top-left - offset (offset is from center to top-left, so we subtract to go from top-left to center)
        centerX = selectedImage.x - offsetX;
        centerY = selectedImage.y - offsetY;

        // Store the fixed center point
        fixedCenterRef.current = {
          x: centerX,
          y: centerY,
          width: selectedImage.width,
          height: selectedImage.height,
        };
      } else {
        // Reuse the stored center point
        centerX = fixedCenterRef.current.x;
        centerY = fixedCenterRef.current.y;
      }

      // Get new rotation angle
      const newRotation = normalizedValue * (Math.PI / 180);

      // The top-left corner relative to center in local (unrotated) coordinates
      const relX = -halfWidth;
      const relY = -halfHeight;

      // Calculate the new relative position vector for the new rotation
      const cosNew = Math.cos(newRotation);
      const sinNew = Math.sin(newRotation);
      const newOffsetX = relX * cosNew - relY * sinNew;
      const newOffsetY = relX * sinNew + relY * cosNew;

      // Calculate new top-left position to keep center fixed
      newX = centerX + newOffsetX;
      newY = centerY + newOffsetY;
    } else {
      // For center-anchored images, just preserve x and y (they already represent the center)
      newX = selectedImage.x;
      newY = selectedImage.y;
    }

    // DEBUG: Log rotation validation attempt
    console.log("DEBUG: ImageEditor rotation validation attempt", {
      requestedRotation: normalizedValue,
      originalRotation: selectedImage.rotation || 0,
      useTopLeftAnchor: useTopLeftAnchor,
      calculatedPosition: { x: newX, y: newY },
      imageDimensions: { width: selectedImage.width, height: selectedImage.height },
      stageDimensions: { width: activeStageWidth || window.innerWidth, height: activeStageHeight || window.innerHeight }
    });

    const canFitAtRequestedRotation = canRotationFitCurrentImageSize(
      normalizedValue,
      selectedImage?.width,
      selectedImage?.height,
    );
    if (!canFitAtRequestedRotation) {
      console.warn("DEBUG: ImageEditor rotation rejected - would clip image", {
        requestedRotation: normalizedValue,
        currentRotation: selectedImage.rotation || 0,
        width: selectedImage?.width,
        height: selectedImage?.height,
        stageWidth: usableStageWidth,
        stageHeight: activeStageHeight,
      });
      // Revert local rotation input to current persisted rotation.
      const fallbackRotation = normalizeRotationValue(selectedImage.rotation || 0);
      setLocalInputValues((prev) => ({
        ...prev,
        rotation: fallbackRotation,
      }));
      pendingManualInputsRef.current.rotation = fallbackRotation;
      return;
    }

    const clampedRotationPos = clampImagePositionForRotation(
      normalizedValue,
      newX,
      newY,
      useTopLeftAnchor,
    );

    // DEBUG: Log clamping results
    console.log("DEBUG: ImageEditor position clamping results", {
      originalPosition: { x: newX, y: newY },
      clampedPosition: clampedRotationPos,
      positionDelta: {
        x: Math.abs(clampedRotationPos.x - newX),
        y: Math.abs(clampedRotationPos.y - newY)
      }
    });

    // For typed rotation input, match transform-end behavior:
    // if rotated image can be kept in-bounds via x/y clamp, apply it.
    const positionChangedByClamp =
      Math.abs(clampedRotationPos.x - newX) > 0.5 ||
      Math.abs(clampedRotationPos.y - newY) > 0.5;

    console.log("DEBUG: ImageEditor rotation validation succeeded - applying rotation", {
      finalRotation: normalizedValue,
      finalPosition: clampedRotationPos,
      usedPositionClamp: positionChangedByClamp,
    });

    updateImageProperties(
      selectedImage.id,
      {
        rotation: normalizedValue,
        x: clampedRotationPos.x,
        y: clampedRotationPos.y,
      },
      false,
      true,
    );
    if (Number.isFinite(clampedRotationPos.x)) {
      pendingManualInputsRef.current.x = clampedRotationPos.x;
    }
    if (Number.isFinite(clampedRotationPos.y)) {
      pendingManualInputsRef.current.y = clampedRotationPos.y;
    }
  };

  const startRotationChange = (direction) => {
    if (rotationIntervalRef.current) return;

    const baseRotation =
      rotationDraftRef.current !== null
        ? rotationDraftRef.current
        : typeof localInputValues.rotation === "number"
          ? localInputValues.rotation
          : normalizeRotationValue(
            localInputValues.rotation ?? selectedImage.rotation ?? 0,
          );

    rotationDraftRef.current = baseRotation;

    const step = () => {
      const current = rotationDraftRef.current ?? 0;
      let nextRotation = current + direction;
      nextRotation = normalizeRotationValue(nextRotation);
      rotationDraftRef.current = nextRotation;
      setLocalInputValues((prev) => ({
        ...prev,
        rotation: nextRotation,
      }));
    };

    step();

    rotationIntervalRef.current = setInterval(() => {
      step();
    }, 100);

    const stopHandler = () => {
      stopRotationChange(true);
    };
    rotationStopHandlerRef.current = stopHandler;
    const stopEvents = [
      "pointerup",
      "pointercancel",
      "mouseup",
      "mouseleave",
      "touchend",
      "touchcancel",
      "blur",
    ];
    stopEvents.forEach((eventName) =>
      window.addEventListener(eventName, stopHandler),
    );
  };

  const stopRotationChange = (shouldFinalize = false) => {
    if (rotationIntervalRef.current) {
      clearInterval(rotationIntervalRef.current);
      rotationIntervalRef.current = null;
    }
    if (rotationStopHandlerRef.current) {
      const stopEvents = [
        "pointerup",
        "pointercancel",
        "mouseup",
        "mouseleave",
        "touchend",
        "touchcancel",
        "blur",
      ];
      stopEvents.forEach((eventName) =>
        window.removeEventListener(eventName, rotationStopHandlerRef.current),
      );
      rotationStopHandlerRef.current = null;
    }
    if (shouldFinalize) {
      finalizeRotationChange();
    } else {
      rotationDraftRef.current = null;
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      stopRotationChange(false);
    };
  }, []);

  const [newReferenceUrl, setNewReferenceUrl] = useState(referenceUrl || "");
  const refLatestRefUrl = useRef(referenceUrl || "");
  const refCurrentImageId = useRef(selectedImage?.id ?? null);

  useEffect(() => {
    let isMounted = true;
    const refresh = () => {
      loadImagePresets().then((presets) => {
        if (isMounted) setImagePresets(presets);
      });
    };
    refresh();
    const onStorage = (e) => {
      if (e.key === IMAGE_PRESETS_STORAGE_KEY || e.key === null) refresh();
    };
    window.addEventListener(IMAGE_PRESETS_CHANGED_EVENT, refresh);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", refresh);
    return () => {
      isMounted = false;
      window.removeEventListener(IMAGE_PRESETS_CHANGED_EVENT, refresh);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  // Sync the local reference URL state from the selected image whenever
  // the selected image or its referenceUrl changes. This ensures that
  // when a new image (for example from a preset) is selected, the input
  // shows that image's reference URL instead of carrying over the last one.
  // Also push to latestImageEditorRefUrlRef so endElementEditing can preserve referenceUrl when user clicks off-canvas before blur.
  useEffect(() => {
    if (selectedImage) {
      const url = selectedImage.referenceUrl || "";
      setNewReferenceUrl(url);
      refLatestRefUrl.current = url;
      if (latestImageEditorRefUrlRef) {
        latestImageEditorRefUrlRef.current = {
          imageId: selectedImage.id,
          referenceUrl: url,
        };
      }
    } else {
      setNewReferenceUrl("");
      refLatestRefUrl.current = "";
      if (latestImageEditorRefUrlRef) {
        latestImageEditorRefUrlRef.current = {
          imageId: null,
          referenceUrl: "",
        };
      }
    }
  }, [
    selectedImage?.id,
    selectedImage?.referenceUrl,
    latestImageEditorRefUrlRef,
  ]);

  // Persist referenceUrl when image is deselected (safety net; instant updates already persist)
  useEffect(() => {
    const currentId = selectedImage?.id ?? null;
    const idWeAreLeaving = refCurrentImageId.current;
    refCurrentImageId.current = currentId;
    return () => {
      if (idWeAreLeaving && updateImageProperties) {
        const urlToSave = refLatestRefUrl.current;
        if (urlToSave !== undefined && urlToSave !== null) {
          updateImageProperties(
            idWeAreLeaving,
            { referenceUrl: urlToSave },
            false,
            true,
            true,
          );
        }
      }
    };
  }, [selectedImage?.id, updateImageProperties]);

  // Local state for input values to avoid immediate updates
  const [localInputValues, setLocalInputValues] = useState({
    x:
      selectedImage?.x !== undefined &&
        selectedImage?.x !== null &&
        Number.isFinite(selectedImage.x)
        ? formatNumericInputValue(selectedImage.x)
        : "",
    y:
      selectedImage?.y !== undefined &&
        selectedImage?.y !== null &&
        Number.isFinite(selectedImage.y)
        ? formatNumericInputValue(selectedImage.y)
        : "",
    width: selectedImage?.width ? selectedImage.width.toFixed(2) : "",
    height: selectedImage?.height ? selectedImage.height.toFixed(2) : "",
    rotation: formatNumericInputValue(selectedImage?.rotation),
  });

  // Clear fixed center ref when selected image changes or is moved/resized
  useEffect(() => {
    // Clear the fixed center ref when image changes (different image selected, moved, or resized)
    fixedCenterRef.current = null;
  }, [
    selectedImage?.id,
    selectedImage?.x,
    selectedImage?.y,
    selectedImage?.width,
    selectedImage?.height,
  ]);

  // Update local state when selectedImage changes (same approach as RectangleEditor)
  // CRITICAL FIX: Update directly like RectangleEditor does - no complex revert detection
  // This ensures drag move updates show immediately in the x/y inputs
  // PERFORMANCE FIX: Use ref to prevent excessive updates during rapid drag moves
  const lastUpdateRef = useRef({
    x: null,
    y: null,
    width: null,
    height: null,
    timestamp: 0,
  });
  const isEditingXRef = useRef(false);
  const isEditingYRef = useRef(false);
  const isEditingWidthRef = useRef(false);
  const isEditingHeightRef = useRef(false);

  useEffect(() => {
    if (selectedImage) {
      // PERFORMANCE FIX: Throttle updates during rapid drag moves to prevent fluctuation
      // Only update if position changed by at least 1 pixel OR 50ms has passed
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateRef.current.timestamp;
      const xChanged =
        Math.abs((selectedImage.x ?? 0) - (lastUpdateRef.current.x ?? 0)) >= 1;
      const yChanged =
        Math.abs((selectedImage.y ?? 0) - (lastUpdateRef.current.y ?? 0)) >= 1;
      const widthChanged =
        lastUpdateRef.current.width == null ||
        Math.abs(
          (selectedImage.width ?? 0) - (lastUpdateRef.current.width ?? 0),
        ) >= 0.5;
      const heightChanged =
        lastUpdateRef.current.height == null ||
        Math.abs(
          (selectedImage.height ?? 0) - (lastUpdateRef.current.height ?? 0),
        ) >= 0.5;
      const shouldUpdate =
        (xChanged || yChanged || widthChanged || heightChanged) &&
        timeSinceLastUpdate >= 16; // ~60fps max

      if (!shouldUpdate && timeSinceLastUpdate < 100) {
        // Skip this update if position hasn't changed enough and we updated recently
        return;
      }

      // Update the ref
      lastUpdateRef.current = {
        x: selectedImage.x ?? 0,
        y: selectedImage.y ?? 0,
        width: selectedImage.width ?? 0,
        height: selectedImage.height ?? 0,
        timestamp: now,
      };

      // Recalculate constraints for this image
      // CRITICAL FIX: Check _isInOffCanvasDuringDrag flag first (for elements being dragged between canvases)
      // If flag is explicitly false, element is on main canvas (don't check x position)
      // If flag is true, element is on off-canvas
      // If flag is undefined, fall back to x position check for elements already on off-canvas
      const currentIsOffCanvas =
        selectedImage?._previewTargetStage === "offCanvas"
          ? true
          : selectedImage?._previewTargetStage === "main"
            ? false
            : (selectedImage?._isDragging === true &&
                selectedImage?.isOffCanvas === true) ||
              (selectedImage?._isDragging === true &&
                selectedImage?._previewTargetStage !== "main" &&
                (selectedImage?.isOffCanvas === true ||
                  selectedImage?._isInOffCanvasDuringDrag === true ||
                  (selectedImage?._isInOffCanvasDuringDrag === undefined &&
                    !selectedImage?.isOnTopLayer &&
                    (selectedImage?.x < 0 ||
                      (selectedImage?.x !== undefined &&
                        selectedImage?.x >= window.innerWidth * 0.45))))) ||
              (selectedImage?._isDragging !== true &&
                (selectedImage?.isOffCanvas === true ||
                  selectedImage?._isInOffCanvasDuringDrag === true ||
                  (selectedImage?._isInOffCanvasDuringDrag === undefined &&
                    !selectedImage?.isOnTopLayer &&
                    (selectedImage?.x < 0 ||
                      (selectedImage?.x !== undefined &&
                        selectedImage?.x >= window.innerWidth * 0.45)))));

      const currentConstraints = currentIsOffCanvas
        ? (() => {
          const stageWidth = getOffCanvasStageWidthPx();
          const usableWidth = getOffCanvasMainColumnBounds(stageWidth).mainZoneStart;
          const stageHeight = offCanvasStageHeight;
          const halfWidth = currentImageWidth / 2;
          const halfHeight = currentImageHeight / 2;
          return {
            minX: halfWidth,
            maxX: Math.max(halfWidth, usableWidth - halfWidth),
            minY: halfHeight,
            maxY: Math.max(halfHeight, stageHeight - halfHeight),
          };
        })()
        : {
          minX: currentImageWidth / 2,
          maxX: canvasPixelWidth - currentImageWidth / 2,
          minY: currentImageHeight / 2,
          maxY: canvasPixelHeight - currentImageHeight / 2,
        };

      // CRITICAL FIX: Update position values directly from selectedImage (like RectangleEditor)
      // Don't constrain during drag - just show the actual values
      // The constraint logic will apply when the user manually edits the input
      const displayX =
        selectedImage.x !== undefined &&
          selectedImage.x !== null &&
          Number.isFinite(selectedImage.x)
          ? selectedImage.x
          : "";
      const displayY =
        selectedImage.y !== undefined &&
          selectedImage.y !== null &&
          Number.isFinite(selectedImage.y)
          ? selectedImage.y
          : "";

      setLocalInputValues((prev) => ({
        ...prev,
        x: isEditingXRef.current
          ? prev.x
          : (() => {
              const pendingX = pendingManualInputsRef.current.x;
              if (Number.isFinite(pendingX)) {
                if (
                  Number.isFinite(displayX) &&
                  Math.abs(Number(displayX) - pendingX) <= 0.01
                ) {
                  pendingManualInputsRef.current.x = null;
                } else {
                  return formatNumericInputValue(pendingX);
                }
              }
              return displayX !== ""
                ? formatNumericInputValue(displayX)
                : "";
            })(),
        y: isEditingYRef.current
          ? prev.y
          : (() => {
              const pendingY = pendingManualInputsRef.current.y;
              if (Number.isFinite(pendingY)) {
                if (
                  Number.isFinite(displayY) &&
                  Math.abs(Number(displayY) - pendingY) <= 0.01
                ) {
                  pendingManualInputsRef.current.y = null;
                } else {
                  return formatNumericInputValue(pendingY);
                }
              }
              return displayY !== ""
                ? formatNumericInputValue(displayY)
                : "";
            })(),
        width: isEditingWidthRef.current
          ? prev.width
          : (() => {
              const pendingWidth = pendingManualInputsRef.current.width;
              if (Number.isFinite(pendingWidth)) {
                const selectedW = Number(selectedImage.width);
                if (
                  Number.isFinite(selectedW) &&
                  Math.abs(selectedW - pendingWidth) <= 0.01
                ) {
                  pendingManualInputsRef.current.width = null;
                } else {
                  return pendingWidth.toFixed(2);
                }
              }
              return selectedImage.width
                ? selectedImage.width.toFixed(2)
                : prev.width;
            })(),
        height: isEditingHeightRef.current
          ? prev.height
          : (() => {
              const pendingHeight = pendingManualInputsRef.current.height;
              const selectedH = Number(selectedImage.height);
              if (Number.isFinite(pendingHeight)) {
                if (
                  Number.isFinite(selectedH) &&
                  Math.abs(selectedH - pendingHeight) <= 0.01
                ) {
                  pendingManualInputsRef.current.height = null;
                } else {
                  return pendingHeight.toFixed(2);
                }
              }
              return selectedImage.height
                ? selectedImage.height.toFixed(2)
                : prev.height;
            })(),
        rotation: isEditingRotation
          ? prev.rotation
          : (() => {
              const pendingRotation = pendingManualInputsRef.current.rotation;
              const selectedRotation = Number(selectedImage.rotation);
              if (Number.isFinite(pendingRotation)) {
                if (
                  Number.isFinite(selectedRotation) &&
                  Math.abs(selectedRotation - pendingRotation) <= 0.01
                ) {
                  pendingManualInputsRef.current.rotation = null;
                } else {
                  return formatNumericInputValue(pendingRotation);
                }
              }
              return formatNumericInputValue(selectedImage.rotation);
            })(),
      }));

      // Update refs to track position for future comparisons
      lastDisplayedPositionRef.current = {
        x: selectedImage.x ?? 0,
        y: selectedImage.y ?? 0,
      };
    }
  }, [
    selectedImage,
    selectedImage?._isInOffCanvasDuringDrag,
    selectedImage?.x,
    selectedImage?.y,
    selectedImage?.width,
    selectedImage?.height,
    selectedImage?.rotation,
    currentImageWidth,
    currentImageHeight,
    canvasPixelWidth,
    canvasPixelHeight,
  ]);

  // Handle reference URL change - update instantly like x, y, rotation (no debounce)
  const handleReferenceUrlChange = (e) => {
    const value = e.target.value;
    setNewReferenceUrl(value);
    refLatestRefUrl.current = value;
    if (latestImageEditorRefUrlRef && selectedImage?.id) {
      latestImageEditorRefUrlRef.current = {
        imageId: selectedImage.id,
        referenceUrl: value,
      };
    }
    if (!selectedImage?.id) return;
    // Same as numeric fields: persist immediately, skipHistorySave so history is saved on unselect
    updateImageProperties(
      selectedImage.id,
      { referenceUrl: value },
      false,
      true,
      true,
    );
  };

  const handleReferenceUrlEditComplete = () => {
    refLatestRefUrl.current = newReferenceUrl;
    if (selectedImage?.id) {
      updateImageProperties(
        selectedImage.id,
        { referenceUrl: newReferenceUrl },
        false,
        true,
        true,
      );
    }
  };

  // Handle reference URL click to navigate
  const handleReferenceUrlClick = () => {
    if (newReferenceUrl && newReferenceUrl.trim() !== "") {
      window.open(newReferenceUrl, "_blank");
    }
  };

  if (!selectedImage) return null;
  // Trigger hidden file input
  const triggerReplace = () => {
    fileInputRef.current?.click();
  };

  const saveImagePresetsToStorage = (presets) => {
    persistImagePresets(presets);
  };

  const saveDefaultImagePresetToStorage = (preset) => {
    if (String(defaultImagePresetId) === String(preset.id)) {
      localStorage.removeItem(IMAGE_DEFAULT_PRESET_STORAGE_KEY);
      localStorage.removeItem(IMAGE_DEFAULT_PRESET_ID_STORAGE_KEY);
      setDefaultImagePresetId(null);
      return;
    }
    localStorage.setItem(
      IMAGE_DEFAULT_PRESET_STORAGE_KEY,
      JSON.stringify(preset),
    );
    localStorage.setItem(
      IMAGE_DEFAULT_PRESET_ID_STORAGE_KEY,
      String(preset.id),
    );
    setDefaultImagePresetId(String(preset.id));
  };

  // Helper for number input: allow empty string, clamp values
  const handleNumberInputWithLimits = (field, value, min, max) => {
    let num = parseFloat(value);
    if (value === "" || Number.isNaN(num)) {
      num = typeof min === "number" ? min : 0;
    }
    if (typeof min === "number" && num < min) num = min;
    if (typeof max === "number" && num > max) num = max;

    // CRITICAL: Pass skipHistorySave=true to prevent immediate history save
    // This ensures all property changes are grouped together and saved as ONE history entry
    // when the element is unselected (via endElementEditing)
    // This matches the behavior of rectangles
    // CRITICAL FIX: Pass isManualInput=true to bypass cooldown period for manual input changes
    // This ensures manual position changes work immediately even after a drag operation
    updateImageProperties(
      selectedImage.id,
      { [field]: num },
      false, // isDragging=false
      true, // skipHistorySave=true (will set pendingChanges for endElementEditing)
      true, // isManualInput=true (bypass cooldown for manual input changes)
    );

    // DO NOT call saveToHistory here - let endElementEditing handle it
    // This ensures all property changes are grouped into one history entry

    return num;
  };

  const handleEnterKey = (event, commitFn) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitFn();
    }
  };

  const commitPositionValue = (field, rawValue, min, max) => {
    const normalizedValue = handleNumberInputWithLimits(
      field,
      rawValue,
      min,
      max,
    );

    // CRITICAL FIX: For off-canvas images, ensure position stays within bounds
    // This acts as a "wall" to prevent going off screen
    let finalValue = normalizedValue;
    if (isOffCanvas) {
      const constraints = getStageConstraints();
      if (field === "x") {
        finalValue = Math.max(
          constraints.minX,
          Math.min(constraints.maxX, normalizedValue),
        );
      } else if (field === "y") {
        finalValue = Math.max(
          constraints.minY,
          Math.min(constraints.maxY, normalizedValue),
        );
      }
    }
    if (field === "y") {
    }

    setLocalInputValues((prev) => ({
      ...prev,
      [field]: formatNumericInputValue(finalValue),
    }));
    pendingManualInputsRef.current[field] = Number(finalValue);
  };

  const commitWidthValue = (rawValue) => {
    if (rawValue === "") {
      const committedWidth = handleWidthChange(MIN_WIDTH);
      pendingManualInputsRef.current.width = Number(committedWidth);
      return;
    }

    const numValue = parseFloat(rawValue);
    if (!Number.isNaN(numValue)) {
      const selectedWidthNow = Number(selectedImage?.width);
      if (
        Number.isFinite(selectedWidthNow) &&
        Math.abs(selectedWidthNow - numValue) <= 0.01
      ) {
        return;
      }
      const committedWidth = handleWidthChange(numValue);
      pendingManualInputsRef.current.width = Number(committedWidth);
    }
  };

  const commitHeightValue = (rawValue) => {
    if (rawValue === "") {
      const committedHeight = handleHeightChange(MIN_HEIGHT);
      pendingManualInputsRef.current.height = Number(committedHeight);
      return;
    }

    const numValue = parseFloat(rawValue);
    if (!Number.isNaN(numValue)) {
      const selectedHeightNow = Number(selectedImage?.height);
      if (
        Number.isFinite(selectedHeightNow) &&
        Math.abs(selectedHeightNow - numValue) <= 0.01
      ) {
        return;
      }
      const committedHeight = handleHeightChange(numValue);
      pendingManualInputsRef.current.height = Number(committedHeight);
    }
  };

  const clampImageCenterForSize = (
    width,
    height,
    { preserveTopLeftForCenterAnchor = true } = {},
  ) => {
    const safeWidth = Math.max(0, Number(width) || 0);
    const safeHeight = Math.max(0, Number(height) || 0);
    const halfWidth = safeWidth / 2;
    const halfHeight = safeHeight / 2;
    const minCenterX = halfWidth + EDITOR_EDGE_BUFFER_PX;
    const maxCenterX = Math.max(
      minCenterX,
      usableStageWidth - halfWidth - EDITOR_EDGE_BUFFER_PX,
    );
    const minCenterY = halfHeight + EDITOR_EDGE_BUFFER_PX;
    const maxCenterY = Math.max(
      minCenterY,
      activeStageHeight - halfHeight - EDITOR_EDGE_BUFFER_PX,
    );

    const currentXRaw = Number(selectedImage?.x);
    const currentYRaw = Number(selectedImage?.y);
    let baseX = Number.isFinite(currentXRaw) ? currentXRaw : minCenterX;
    let baseY = Number.isFinite(currentYRaw) ? currentYRaw : minCenterY;

    // Manual width/height typing should expand from the current top-left edge,
    // not from center, so top-edge images do not jump outside bounds.
    if (
      preserveTopLeftForCenterAnchor &&
      Number.isFinite(currentXRaw) &&
      Number.isFinite(currentYRaw)
    ) {
      const prevWidth = Math.max(0, Number(selectedImage?.width) || 0);
      const prevHeight = Math.max(0, Number(selectedImage?.height) || 0);
      const prevTopLeftX = currentXRaw - prevWidth / 2;
      const prevTopLeftY = currentYRaw - prevHeight / 2;
      baseX = prevTopLeftX + halfWidth;
      baseY = prevTopLeftY + halfHeight;
    }

    const clampedX = Math.max(minCenterX, Math.min(maxCenterX, baseX));
    const clampedY = Math.max(minCenterY, Math.min(maxCenterY, baseY));

    return { clampedX, clampedY };
  };

  // Handle width change with aspect ratio consideration
  const handleWidthChange = (newWidth) => {
    const widthMax = Math.max(
      MIN_WIDTH,
      getRotationAwareMaxWidthByStageFit(Number(selectedImage?.height) || MIN_HEIGHT),
    );
    // Ensure newWidth is a valid number and apply constraints
    const constrainedWidth = Math.max(
      MIN_WIDTH,
      Math.min(widthMax, newWidth),
    );

    if (maintainAspectRatio && selectedImage.height) {
      // Use the stored original aspect ratio for consistency, fallback to current dimensions
      let aspectRatio = selectedImage.originalAspectRatio;

      if (!aspectRatio || aspectRatio <= 0) {
        aspectRatio = selectedImage.width / selectedImage.height;
      }

      // Calculate pair while preserving aspect ratio even after stage caps.
      let finalWidth = constrainedWidth;
      let finalHeight = Math.round((finalWidth / aspectRatio) * 100) / 100;
      const maxHeightForCurrentWidth = getRotationAwareMaxHeightByStageFit(
        finalWidth,
      );
      if (finalHeight > maxHeightForCurrentWidth) {
        finalHeight = Math.max(
          MIN_HEIGHT,
          Math.min(maxHeightForCurrentWidth, finalHeight),
        );
        finalWidth = Math.max(
          MIN_WIDTH,
          Math.min(
            getRotationAwareMaxWidthByStageFit(finalHeight),
            Math.round(finalHeight * aspectRatio * 100) / 100,
          ),
        );
      } else {
        finalHeight = Math.max(
          MIN_HEIGHT,
          Math.min(maxHeightForCurrentWidth, finalHeight),
        );
      }
      const clampedPos = clampImagePositionForRotation(
        Number(selectedImage?.rotation) || 0,
        Number(selectedImage?.x) || 0,
        Number(selectedImage?.y) || 0,
        selectedImage?.useTopLeftAnchor === true,
        finalWidth,
        finalHeight,
      );

      // Update local input values to keep them in sync
      setLocalInputValues((prev) => ({
        ...prev,
        width: finalWidth.toFixed(2),
        height: finalHeight.toFixed(2),
      }));

      // CRITICAL: Pass skipHistorySave=true to prevent immediate history save
      // This ensures all property changes are grouped together
      updateImageProperties(
        selectedImage.id,
        {
          width: finalWidth,
          height: finalHeight,
          x: clampedPos.x,
          y: clampedPos.y,
          skipAspectRatioCorrection: true,
          // Ensure aspect ratio is preserved in the update
          originalAspectRatio: aspectRatio,
        },
        false, // isDragging=false
        true, // skipHistorySave=true
        true, // isManualInput=true (bypass drag-end cooldown for clamped x/y)
      );
      pendingManualInputsRef.current.width = Number(finalWidth);
      pendingManualInputsRef.current.height = Number(finalHeight);
      pendingManualInputsRef.current.x = Number(clampedPos.x);
      pendingManualInputsRef.current.y = Number(clampedPos.y);
      return finalWidth;
    } else {
      const clampedPos = clampImagePositionForRotation(
        Number(selectedImage?.rotation) || 0,
        Number(selectedImage?.x) || 0,
        Number(selectedImage?.y) || 0,
        selectedImage?.useTopLeftAnchor === true,
        constrainedWidth,
        Number(selectedImage?.height) || 0,
      );

      // Update local input values
      setLocalInputValues((prev) => ({
        ...prev,
        width: constrainedWidth.toFixed(2),
      }));

      // CRITICAL: Pass skipHistorySave=true to prevent immediate history save
      updateImageProperties(
        selectedImage.id,
        {
          width: constrainedWidth,
          x: clampedPos.x,
          y: clampedPos.y,
        },
        false, // isDragging=false
        true, // skipHistorySave=true
        true, // isManualInput=true (bypass drag-end cooldown for clamped x/y)
      );
      pendingManualInputsRef.current.width = Number(constrainedWidth);
      pendingManualInputsRef.current.x = Number(clampedPos.x);
      pendingManualInputsRef.current.y = Number(clampedPos.y);
      return constrainedWidth;
    }
  };

  // Handle height change with aspect ratio consideration
  const handleHeightChange = (newHeight) => {
    const heightMax = Math.max(
      MIN_HEIGHT,
      getRotationAwareMaxHeightByStageFit(
        Number(selectedImage?.width) || MIN_WIDTH,
      ),
    );
    // Ensure newHeight is a valid number and apply constraints
    const constrainedHeight = Math.max(
      MIN_HEIGHT,
      Math.min(heightMax, newHeight),
    );

    if (maintainAspectRatio && selectedImage.width) {
      // Use the stored original aspect ratio for consistency, fallback to current dimensions
      let aspectRatio = selectedImage.originalAspectRatio;

      if (!aspectRatio || aspectRatio <= 0) {
        aspectRatio = selectedImage.width / selectedImage.height;
      }

      // Calculate pair while preserving aspect ratio even after stage caps.
      let finalHeight = constrainedHeight;
      let finalWidth = Math.round(finalHeight * aspectRatio * 100) / 100;
      const maxWidthForCurrentHeight = getRotationAwareMaxWidthByStageFit(
        finalHeight,
      );
      if (finalWidth > maxWidthForCurrentHeight) {
        finalWidth = Math.max(
          MIN_WIDTH,
          Math.min(maxWidthForCurrentHeight, finalWidth),
        );
        finalHeight = Math.max(
          MIN_HEIGHT,
          Math.min(
            getRotationAwareMaxHeightByStageFit(finalWidth),
            Math.round((finalWidth / aspectRatio) * 100) / 100,
          ),
        );
      } else {
        finalWidth = Math.max(
          MIN_WIDTH,
          Math.min(maxWidthForCurrentHeight, finalWidth),
        );
      }
      const clampedPos = clampImagePositionForRotation(
        Number(selectedImage?.rotation) || 0,
        Number(selectedImage?.x) || 0,
        Number(selectedImage?.y) || 0,
        selectedImage?.useTopLeftAnchor === true,
        finalWidth,
        finalHeight,
      );

      // Update local input values to keep them in sync
      setLocalInputValues((prev) => ({
        ...prev,
        width: finalWidth.toFixed(2),
        height: finalHeight.toFixed(2),
      }));

      // CRITICAL: Pass skipHistorySave=true to prevent immediate history save
      // This ensures all property changes are grouped together
      updateImageProperties(
        selectedImage.id,
        {
          width: finalWidth,
          height: finalHeight,
          x: clampedPos.x,
          y: clampedPos.y,
          skipAspectRatioCorrection: true,
          // Ensure aspect ratio is preserved in the update
          originalAspectRatio: aspectRatio,
        },
        false, // isDragging=false
        true, // skipHistorySave=true
        true, // isManualInput=true (bypass drag-end cooldown for clamped x/y)
      );
      pendingManualInputsRef.current.width = Number(finalWidth);
      pendingManualInputsRef.current.height = Number(finalHeight);
      pendingManualInputsRef.current.x = Number(clampedPos.x);
      pendingManualInputsRef.current.y = Number(clampedPos.y);
      return finalHeight;
    } else {
      const clampedPos = clampImagePositionForRotation(
        Number(selectedImage?.rotation) || 0,
        Number(selectedImage?.x) || 0,
        Number(selectedImage?.y) || 0,
        selectedImage?.useTopLeftAnchor === true,
        Number(selectedImage?.width) || 0,
        constrainedHeight,
      );

      // Update local input values
      setLocalInputValues((prev) => ({
        ...prev,
        height: constrainedHeight.toFixed(2),
      }));

      // CRITICAL: Pass skipHistorySave=true to prevent immediate history save
      updateImageProperties(
        selectedImage.id,
        {
          height: constrainedHeight,
          x: clampedPos.x,
          y: clampedPos.y,
        },
        false, // isDragging=false
        true, // skipHistorySave=true
        true, // isManualInput=true (bypass drag-end cooldown for clamped x/y)
      );
      pendingManualInputsRef.current.height = Number(constrainedHeight);
      pendingManualInputsRef.current.x = Number(clampedPos.x);
      pendingManualInputsRef.current.y = Number(clampedPos.y);
      return constrainedHeight;
    }
  };

  const applyImagePreset = (preset) => {
    if (!selectedImage) return;

    const img = new Image();
    img.onload = async () => {
      // Compress the preset image by default
      const compressedImg = await compressImage(img);

      let updatedImagesArray = null;
      const originalWidth = img.naturalWidth || img.width || selectedImage.width;
      const originalHeight = img.naturalHeight || img.height || selectedImage.height;
      const originalAspectRatio =
        originalHeight > 0
          ? originalWidth / originalHeight
          : selectedImage.originalAspectRatio || 1;

      // Only replace image bitmap/source; keep current element placement/transforms.
      if (updateImageProperties) {
        updateImageProperties(
          selectedImage.id,
          {
            image: compressedImg,
            src: preset.src,
            originalImage: img,
            compressedImage: compressedImg,
            compress: true,
            originalImageDataUrl: preset.src,
            imageDataUrl: preset.src,
            originalWidth,
            originalHeight,
            originalAspectRatio,
            cropData: null,
            originalCropRotation: undefined,
            uncompressedCroppedImage: undefined,
            _replacedAt: Date.now(),
            isReplacement: true,
          },
          false,
          true,
        );
      } else {
        setImages((prev) => {
          const updated = prev.map((existingImage) =>
            existingImage.id === selectedImage.id
              ? {
                ...existingImage,
                image: compressedImg,
                src: preset.src,
                originalImage: img,
                compressedImage: compressedImg,
                compress: true,
                originalImageDataUrl: preset.src,
                imageDataUrl: preset.src,
                originalWidth,
                originalHeight,
                originalAspectRatio,
                cropData: null,
                originalCropRotation: undefined,
                uncompressedCroppedImage: undefined,
                _replacedAt: Date.now(),
              }
              : existingImage,
          );
          updatedImagesArray = updated;
          return updated;
        });
      }

      // Save to history after replacing image from preset
      if (saveToHistory && updatedImagesArray) {
        setTimeout(() => {
          saveToHistory("replace_image", updatedImagesArray);
        }, 100);
      }
    };
    img.src = preset.src;
  };

  // Helper function to convert Image object to data URL
  const imageToDataURL = (image) => {
    if (!image || !(image instanceof HTMLImageElement)) return null;

    try {
      const canvas = document.createElement("canvas");
      canvas.width = image.width || image.naturalWidth;
      canvas.height = image.height || image.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0);
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Failed to convert image to data URL:", error);
      return image.src; // Fallback to original src
    }
  };

  /** Sample downscaled pixels — JPEG compression path destroys alpha (shows black). */
  const imageHasTransparency = (img) => {
    if (!img || !(img instanceof HTMLImageElement)) return false;
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) return false;
    const maxSide = 256;
    const w = Math.min(iw, maxSide);
    const h = Math.min(ih, maxSide);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, iw, ih, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) return true;
      }
    } catch {
      return false;
    }
    return false;
  };

  // Helper function to compress images with moderate compression using browser-image-compression
  const compressImage = async (originalImage, quality = 0.5) => {
    return new Promise(async (resolve) => {
      try {
        const preserveAlpha = imageHasTransparency(originalImage);
        const mime = preserveAlpha ? "image/png" : "image/jpeg";

        const canvas = document.createElement("canvas");
        canvas.width = originalImage.width || originalImage.naturalWidth;
        canvas.height = originalImage.height || originalImage.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(originalImage, 0, 0);

        const blob = await new Promise((resolveBlob) => {
          if (preserveAlpha) {
            canvas.toBlob((b) => resolveBlob(b), "image/png");
          } else {
            canvas.toBlob((b) => resolveBlob(b), "image/jpeg", quality);
          }
        });

        if (!blob) {
          resolve(originalImage);
          return;
        }

        const options = preserveAlpha
          ? {
              maxSizeMB: 4,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              initialQuality: 1,
              fileType: "image/png",
              alwaysKeepResolution: false,
            }
          : {
              maxSizeMB: 0.2,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              initialQuality: quality,
              fileType: "image/jpeg",
              alwaysKeepResolution: false,
            };

        const compressedBlob = await imageCompression(blob, options);

        const compressedDataUrl =
          await imageCompression.getDataUrlFromFile(compressedBlob);
        const compressed = new Image();
        compressed.onload = () => resolve(compressed);
        compressed.onerror = () => {
          resolve(originalImage);
        };
        compressed.src = compressedDataUrl;
      } catch (error) {
        console.error("Compression error:", error);
        resolve(originalImage);
      }
    });
  };

  const handleReplaceImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // CRITICAL FIX: Clear the file input value so the same file can be selected again
    // This ensures onChange fires even if the user selects the same file
    e.target.value = "";

    // CRITICAL FIX: Clear transformer BEFORE starting replacement to prevent errors
    // When the key changes, React will unmount the old component, and the transformer
    // will have a stale reference to the old node
    if (transformerRef && transformerRef.current) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer()?.batchDraw();
    }

    // Check if it's an SVG file
    const isSvg =
      file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");

    setIsReplacingImage(true);
    // Show global loading overlay
    if (setIsImageLoading) {
      setIsImageLoading(true);
      setImageLoadingProgress(5);
    }

    const reader = new FileReader();
    reader.onload = async () => {
      let newUrl = reader.result;

      if (setImageLoadingProgress) {
        setImageLoadingProgress(20);
      }

      // Convert SVG to JPEG for consistent cropping behavior
      if (isSvg) {
        if (setImageLoadingProgress) {
          setImageLoadingProgress(30);
        }
        try {
          newUrl = await convertSvgToJpeg(newUrl);
          if (setImageLoadingProgress) {
            setImageLoadingProgress(40);
          }
        } catch (error) {
          console.error("Error converting SVG to JPEG:", error);
          setIsReplacingImage(false);
          if (setIsImageLoading) {
            setIsImageLoading(false);
            setImageLoadingProgress(0);
          }
          alert(`Failed to convert SVG to JPEG: ${error.message}`);
          return;
        }
      }

      // Create a new Image object for loading
      const tempImg = new Image(); // This is correct, creates HTMLImageElement

      // Attach onload handler BEFORE setting src
      tempImg.onload = async () => {
        if (setImageLoadingProgress) {
          setImageLoadingProgress(isSvg ? 50 : 40);
        }

        // Get the true natural dimensions exactly like addImage does
        const originalWidth = tempImg.naturalWidth;
        const originalHeight = tempImg.naturalHeight;

        // Use the EXACT same scaling logic as addImage function
        const maxDimension = 300;
        let scaledWidth = originalWidth;
        let scaledHeight = originalHeight;

        if (originalWidth > maxDimension || originalHeight > maxDimension) {
          const scaleFactor = Math.min(
            maxDimension / originalWidth,
            maxDimension / originalHeight,
          );
          scaledWidth = originalWidth * scaleFactor;
          scaledHeight = originalHeight * scaleFactor;
        }

        const newWidth = scaledWidth;
        const newHeight = scaledHeight;

        if (setImageLoadingProgress) {
          setImageLoadingProgress(60);
        }

        // Compress the image by default to save memory
        const compressedImg = await compressImage(tempImg);

        if (setImageLoadingProgress) {
          setImageLoadingProgress(80);
        }

        // Store the current position and rotation before replacement (preserve on replace)
        const currentX = selectedImage.x;
        const currentY = selectedImage.y;
        const currentRotation = selectedImage.rotation ?? 0;
        const keepMaintainAspectRatio =
          selectedImage?.maintainAspectRatio !== undefined
            ? selectedImage.maintainAspectRatio
            : true;
        const useTopLeftAnchor = selectedImage?.useTopLeftAnchor === true;
        const clampedReplacementPos = clampImagePositionForRotation(
          currentRotation,
          currentX,
          currentY,
          useTopLeftAnchor,
          newWidth,
          newHeight,
        );

        // CRITICAL FIX: Add a unique marker to the new image element to help React detect the change
        // This ensures the image object reference is truly different
        if (compressedImg) {
          compressedImg._imageId = `replaced-${Date.now()}`;
        }

        // Create the updated image object - preserve rotation and anchor; reset other transforms
        const updatedImage = {
          ...selectedImage,
          // Use the EXACT same properties as addImage creates
          image: compressedImg, // Use compressed image
          width: newWidth, // Scaled width (same as addImage)
          height: newHeight, // Scaled height (same as addImage)
          src: newUrl,
          originalImage: tempImg, // Store original uncompressed
          compressedImage: compressedImg, // Store compressed version
          // Use same defaults as addImage
          maintainAspectRatio: keepMaintainAspectRatio,
          compress: true, // Compression enabled by default
          // Preserve rotation and useTopLeftAnchor; reset scale and flip
          scaleX: 1,
          scaleY: 1,
          rotation: currentRotation,
          flipHorizontal: false,
          flipVertical: false,
          // Store original dimensions exactly like addImage
          originalWidth: newWidth, // Store scaled dimensions as "original" for this image
          originalHeight: newHeight,
          originalAspectRatio: newWidth / newHeight, // Aspect ratio of scaled dimensions
          // Clear ALL crop and transform data since this is a new image
          cropData: null,
          originalImageDataUrl: newUrl,
          imageDataUrl: newUrl,
          originalCropRotation: undefined,
          uncompressedCroppedImage: undefined,
          type: "Image", // Ensure type is set for transformer
          // Keep position
          x: clampedReplacementPos.x,
          y: clampedReplacementPos.y,
          // CRITICAL: Add _replacedAt timestamp immediately
          _replacedAt: Date.now(),
        };

        // Capture the updated images array to pass to saveToHistory
        let updatedImagesArray = null;

        // CRITICAL FIX: Use updateImageProperties to handle both main and off-canvas images
        if (updateImageProperties) {
          updateImageProperties(
            selectedImage.id,
            {
              image: compressedImg,
              width: newWidth,
              height: newHeight,
              src: newUrl,
              originalImage: tempImg,
              compressedImage: compressedImg,
              maintainAspectRatio: keepMaintainAspectRatio,
              compress: true,
              scaleX: 1,
              scaleY: 1,
              rotation: currentRotation,
              flipHorizontal: false,
              flipVertical: false,
              originalWidth: newWidth,
              originalHeight: newHeight,
              originalAspectRatio: newWidth / newHeight,
              cropData: null,
              originalImageDataUrl: newUrl,
              imageDataUrl: newUrl,
              originalCropRotation: undefined,
              uncompressedCroppedImage: undefined,
              type: "Image",
              x: clampedReplacementPos.x,
              y: clampedReplacementPos.y,
              _replacedAt: Date.now(),
              isReplacement: true, // Flag to skip aspect ratio correction
            },
            false, // isDragging
            true, // skipHistorySave (we'll save manually below)
          );
        } else {
          // Fallback: use setImages directly (only updates main canvas)
          setImages((prevImages) => {
            const updated = prevImages.map((img) =>
              img.id === selectedImage.id ? updatedImage : img,
            );
            updatedImagesArray = updated; // Capture for saveToHistory
            // Store in ref immediately so element_edit_complete (on unselect) saves the new image state
            if (latestUpdatedImagesRef) {
              if (!latestUpdatedImagesRef.current) {
                latestUpdatedImagesRef.current = { main: null, offCanvas: null };
              }
              // CRITICAL: Store in the correct canvas based on isOffCanvas flag
              if (isOffCanvas) {
                latestUpdatedImagesRef.current.offCanvas = updated;
              } else {
                latestUpdatedImagesRef.current.main = updated;
              }
            }
            // CRITICAL: Mark pending changes so endElementEditing saves on unselect (otherwise no history entry is pushed)
            if (setPendingChanges) setPendingChanges(true);
            if (pendingChangesRef) pendingChangesRef.current = true;
            return updated;
          });
        }

        if (setImageLoadingProgress) {
          setImageLoadingProgress(90);
        }

        // Use setTimeout to ensure state updates complete before updating selections
        setTimeout(() => {
          // Clear transformer first
          if (transformerRef && transformerRef.current) {
            transformerRef.current.nodes([]);
          }

          // Update selectedObject to reflect the new dimensions
          if (setSelectedObject) {
            setSelectedObject({
              ...updatedImage,
              type: "Image",
            });
          }

          // Also update selectedObjects array if it exists
          if (setSelectedObjects) {
            setSelectedObjects([updatedImage]);
          }

          // Update local input values to reflect the new dimensions (preserve rotation)
          setLocalInputValues((prev) => ({
            ...prev,
            width: newWidth.toFixed(2),
            height: newHeight.toFixed(2),
            rotation: currentRotation,
          }));

          if (setImageLoadingProgress) {
            setImageLoadingProgress(100);
          }

          // Save to history after state update - pass the updated images array
          if (saveToHistory && updatedImagesArray) {
            setTimeout(() => {
              saveToHistory("replace_image", updatedImagesArray);
            }, 100);
          }

          // Clear loading state after a brief delay
          setTimeout(() => {
            setIsReplacingImage(false);
            if (setIsImageLoading) {
              setIsImageLoading(false);
              setImageLoadingProgress(0);
            }

            // CRITICAL FIX: Clear the _replacedAt flag after a delay
            // This stops the force-update logic from running repeatedly
            setTimeout(() => {
              setImages((prevImages) =>
                prevImages.map((img) =>
                  img.id === selectedImage.id && img._replacedAt
                    ? { ...img, _replacedAt: undefined }
                    : img,
                ),
              );
            }, 500);
          }, 100);
        }, 50);
      };

      tempImg.onerror = () => {
        setIsReplacingImage(false);
        if (setIsImageLoading) {
          setIsImageLoading(false);
          setImageLoadingProgress(0);
        }
        alert("Failed to load replacement image");
      };

      // Set src to start loading
      tempImg.src = newUrl;
    };

    reader.onerror = () => {
      setIsReplacingImage(false);
      if (setIsImageLoading) {
        setIsImageLoading(false);
        setImageLoadingProgress(0);
      }
      alert("Failed to read image file");
    };

    reader.readAsDataURL(file);
  };

  const handleNumberInput = (field, value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      updateImageProperties(selectedImage.id, { [field]: numValue });
    }
  };

  const rotate = (direction) => {
    if (!selectedImage) return;

    const oldRotation = selectedImage.rotation || 0;
    let newRotation = oldRotation + direction;

    // Normalize to 0-360 range
    newRotation = newRotation % 360;
    if (newRotation < 0) {
      newRotation += 360;
    }

    // CRITICAL FIX: For images with useTopLeftAnchor, we need to preserve the center point during rotation
    // not the top-left corner. This ensures rotation happens around the center, just like the transformer handle.
    // This matches the behavior of finalizeRotationChange to ensure consistent rotation behavior.
    const useTopLeftAnchor = selectedImage.useTopLeftAnchor === true;
    let newX = selectedImage.x;
    let newY = selectedImage.y;

    if (useTopLeftAnchor) {
      // CRITICAL FIX: Calculate or reuse the fixed center point
      // On first rotation, calculate and store the center point
      // On subsequent rotations, reuse the stored center point to prevent drift
      const halfWidth = selectedImage.width / 2;
      const halfHeight = selectedImage.height / 2;
      const currentRotation = (selectedImage.rotation || 0) * (Math.PI / 180);

      let centerX, centerY;

      // If we don't have a stored center point, or if the image dimensions changed, calculate it
      if (
        !fixedCenterRef.current ||
        fixedCenterRef.current.width !== selectedImage.width ||
        fixedCenterRef.current.height !== selectedImage.height
      ) {
        // Calculate the true center point by "unrotating" the current position
        // The top-left corner relative to center in local (unrotated) coordinates
        const relX = -halfWidth;
        const relY = -halfHeight;

        // Rotate this relative vector by the current rotation to get the offset from top-left to center
        const cosCurrent = Math.cos(currentRotation);
        const sinCurrent = Math.sin(currentRotation);
        const offsetX = relX * cosCurrent - relY * sinCurrent;
        const offsetY = relX * sinCurrent + relY * cosCurrent;

        // Calculate the true center point in world coordinates
        // Center = top-left - offset (offset is from center to top-left, so we subtract to go from top-left to center)
        centerX = selectedImage.x - offsetX;
        centerY = selectedImage.y - offsetY;

        // Store the fixed center point
        fixedCenterRef.current = {
          x: centerX,
          y: centerY,
          width: selectedImage.width,
          height: selectedImage.height,
        };
      } else {
        // Reuse the stored center point
        centerX = fixedCenterRef.current.x;
        centerY = fixedCenterRef.current.y;
      }

      // Get new rotation angle
      const newRotationRad = newRotation * (Math.PI / 180);

      // The top-left corner relative to center in local (unrotated) coordinates
      const relX = -halfWidth;
      const relY = -halfHeight;

      // Calculate the new relative position vector for the new rotation
      const cosNew = Math.cos(newRotationRad);
      const sinNew = Math.sin(newRotationRad);
      const newOffsetX = relX * cosNew - relY * sinNew;
      const newOffsetY = relX * sinNew + relY * cosNew;

      // Calculate new top-left position to keep center fixed
      newX = centerX + newOffsetX;
      newY = centerY + newOffsetY;
    } else {
      // For center-anchored images, just preserve x and y (they already represent the center)
      newX = selectedImage.x;
      newY = selectedImage.y;
    }

    // CRITICAL: Pass skipHistorySave=true to prevent saving to history on each rotation change
    // History will be saved when element is unselected via element_edit_complete
    // This groups all rotation changes (from -90/+90 buttons) into a single history entry
    // Similar to how finalizeRotationChange handles rotation input changes
    // For +/-90 quick-rotate buttons, do not auto-shift when rotated bounds overflow.
    // Keep behavior strict: if rotation at current anchor position doesn't fit stage,
    // reject the action.
    if (!doesRotationFitAtPosition(newRotation, newX, newY, useTopLeftAnchor)) {
      return;
    }

    updateImageProperties(
      selectedImage.id,
      {
        rotation: newRotation,
        x: newX,
        y: newY,
      },
      false,
      true,
    ); // isDragging=false, skipHistorySave=true
  };

  const handleCropClick = () => {
    // Set ref immediately so any same-tick click (e.g. from this button) won't clear selection
    setCropModalOpenRefOnly();
    setIsCropLoading(true);
    // Use setTimeout to show loader before heavy modal rendering
    setTimeout(() => {
      setIsCropModalOpen(true);
      setIsCropLoading(false);
    }, 50);
  };

  const handleCropModalClose = () => {
    setIsCropModalOpen(false);
  };

  const handleCropConfirm = async ({
    croppedImage,
    croppedImageUrl,
    cropData,
    originalImageData,
    rotationAngle,
    isRotatedImage,
    originalRotation,
    flipHorizontal,
    flipVertical,
    canvasWidth,
    canvasHeight,
  }) => {
    // Ensure the cropped image is fully loaded before updating
    if (croppedImage.complete && croppedImage.naturalWidth > 0) {
      // Store the original uncompressed cropped image
      const uncompressedCroppedImage = croppedImage;
      const uncompressedCroppedImageUrl = croppedImageUrl;

      // PERFORMANCE: Defer heavy compression work to a background task so
      // confirming the crop feels instant. We initially apply the uncompressed
      // cropped image, then (optionally) swap in a compressed bitmap later.
      let compressedCroppedImage = null;
      let compressedCroppedImageUrl = null;

      // Use the uncompressed version for the immediate UI update
      let finalCroppedImage = uncompressedCroppedImage;
      let finalCroppedImageUrl = uncompressedCroppedImageUrl;

      // Get the true original image - prioritize originalImageData from crop modal,
      // then fall back to selectedImage.originalImage, then selectedImage.image
      const trueOriginalImage =
        originalImageData || selectedImage.originalImage || selectedImage.image;

      // CRITICAL: Always preserve the very first original image across multiple crops
      // If this is the first crop (no existing originalImageDataUrl), create it
      // If this is a subsequent crop, keep the existing originalImageDataUrl
      let originalImageDataUrl = selectedImage.originalImageDataUrl;

      // Only create originalImageDataUrl if it doesn't exist (first crop)
      if (!originalImageDataUrl && trueOriginalImage) {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = trueOriginalImage.naturalWidth;
          canvas.height = trueOriginalImage.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(trueOriginalImage, 0, 0);
          originalImageDataUrl = canvas.toDataURL("image/png");
        } catch (error) {
          originalImageDataUrl = trueOriginalImage.src;
        }
      } else if (originalImageDataUrl) {
      }

      // CRITICAL FIX: Calculate display size to maintain visual consistency
      // The cropper works with full-resolution images, but we need to scale the result
      // relative to the FIRST original display size, not compound it with each crop

      // Get the natural dimensions of the cropped image (full resolution from cropper)
      const croppedNaturalWidth = croppedImage.naturalWidth;
      const croppedNaturalHeight = croppedImage.naturalHeight;

      // Get the FIRST original image's natural dimensions (the very first uncropped version)
      const originalNaturalWidth = trueOriginalImage.naturalWidth;
      const originalNaturalHeight = trueOriginalImage.naturalHeight;

      // CRITICAL FIX: Use CURRENT cropped dimensions as baseline when image is already cropped
      // This prevents the image from appearing larger when rotation is applied after crop+flip
      // If the image is already cropped, use the current cropped dimensions as baseline
      // Otherwise, use the first original dimensions
      const isAlreadyCropped =
        selectedImage.cropData &&
        (selectedImage.width !== selectedImage.originalWidth ||
          selectedImage.height !== selectedImage.originalHeight);

      // Get the FIRST original display size (before any cropping)
      // This is used for originalWidth/originalHeight preservation.
      // IMPORTANT: On FIRST crop (no existing cropData), treat the current on-canvas
      // dimensions as the "original" so manual resizes (e.g. 300 → 113) are preserved.
      const firstOriginalWidth =
        selectedImage.cropData == null
          ? selectedImage.width || selectedImage.originalWidth || 100
          : selectedImage.originalWidth || selectedImage.width || 100;
      const firstOriginalHeight =
        selectedImage.cropData == null
          ? selectedImage.height || selectedImage.originalHeight || 100
          : selectedImage.originalHeight || selectedImage.height || 100;

      // For baseline, use current cropped dimensions if already cropped, otherwise use original
      const baselineWidth = isAlreadyCropped
        ? selectedImage.width
        : firstOriginalWidth;
      const baselineHeight = isAlreadyCropped
        ? selectedImage.height
        : firstOriginalHeight;

      // CRITICAL: Only when user hit "Reset" in advanced crop modal do we restore original display size (firstOriginalWidth/Height).
      // For any actual crop (small portion), use the ratio logic so width/height = cropped portion dimensions.
      // Detect "full image" only when the crop rectangle is the entire original image:
      // 1) Normalized coords: (0,0,1,1) - what the cropper uses after Reset
      // 2) Pixel coords matching true original natural size (0,0,originalNaturalWidth,originalNaturalHeight)
      // Do NOT use "crop matches cropper output size" - that is true for every crop (output is always 0,0,w,h of the cropped region).
      const normalizedFull =
        cropData &&
        Math.abs(cropData.left ?? 0) < 0.002 &&
        Math.abs(cropData.top ?? 0) < 0.002 &&
        Math.abs((cropData.width ?? 1) - 1) < 0.002 &&
        Math.abs((cropData.height ?? 1) - 1) < 0.002;
      const pixelFullOriginal =
        cropData &&
        originalNaturalWidth > 0 &&
        originalNaturalHeight > 0 &&
        Math.abs(cropData.left ?? 0) < 1 &&
        Math.abs(cropData.top ?? 0) < 1 &&
        Math.abs((cropData.width ?? 0) - originalNaturalWidth) < 2 &&
        Math.abs((cropData.height ?? 0) - originalNaturalHeight) < 2;
      const isFullImageCrop = normalizedFull || pixelFullOriginal;

      let finalDisplayWidth, finalDisplayHeight;

      if (isFullImageCrop) {
        if (!selectedImage.cropData) {
          finalDisplayWidth = selectedImage.width || firstOriginalWidth;
          finalDisplayHeight = selectedImage.height || firstOriginalHeight;
        } else {
          const maxDimension = 300;
          const originalIsNaturalSize =
            originalNaturalWidth > 0 &&
            originalNaturalHeight > 0 &&
            Math.abs(firstOriginalWidth - originalNaturalWidth) < 2 &&
            Math.abs(firstOriginalHeight - originalNaturalHeight) < 2;
          if (
            originalIsNaturalSize &&
            (firstOriginalWidth > maxDimension ||
              firstOriginalHeight > maxDimension)
          ) {
            const scale = Math.min(
              maxDimension / firstOriginalWidth,
              maxDimension / firstOriginalHeight,
              1,
            );
            finalDisplayWidth = firstOriginalWidth * scale;
            finalDisplayHeight = firstOriginalHeight * scale;
          } else {
            finalDisplayWidth = firstOriginalWidth;
            finalDisplayHeight = firstOriginalHeight;
          }
        }
      } else {
        let cropWidthRatio, cropHeightRatio;

        if (isAlreadyCropped) {
          const currentCroppedNaturalWidth =
            selectedImage.image?.naturalWidth || croppedNaturalWidth;
          const currentCroppedNaturalHeight =
            selectedImage.image?.naturalHeight || croppedNaturalHeight;

          cropWidthRatio = croppedNaturalWidth / currentCroppedNaturalWidth;
          cropHeightRatio = croppedNaturalHeight / currentCroppedNaturalHeight;
        } else {
          cropWidthRatio = croppedNaturalWidth / originalNaturalWidth;
          cropHeightRatio = croppedNaturalHeight / originalNaturalHeight;
        }

        finalDisplayWidth = baselineWidth * cropWidthRatio;
        finalDisplayHeight = baselineHeight * cropHeightRatio;

      }

      // Apply a safety maximum to prevent extremely large results (skip when restoring full image)
      const maxDimension = 300;
      if (
        !isFullImageCrop &&
        (finalDisplayWidth > maxDimension || finalDisplayHeight > maxDimension)
      ) {
        const scaleFactor = Math.min(
          maxDimension / finalDisplayWidth,
          maxDimension / finalDisplayHeight,
        );
        finalDisplayWidth = finalDisplayWidth * scaleFactor;
        finalDisplayHeight = finalDisplayHeight * scaleFactor;
      }

      // Calculate final rotation to save
      let finalRotation = 0;

      // CRITICAL FIX: Check if flip values have changed from the current state
      // If flips haven't changed and were already baked, don't rebake them
      const currentFlipH = selectedImage.flipHorizontal || false;
      const currentFlipV = selectedImage.flipVertical || false;
      const flipChanged =
        flipHorizontal !== currentFlipH || flipVertical !== currentFlipV;

      // Check if rotation changed from the stored originalCropRotation
      const currentCropRotation = selectedImage.originalCropRotation || 0;
      const rotationChanged = rotationAngle !== currentCropRotation;

      // UPDATED ROTATION/FLIP LOGIC:
      // - If transforms changed (rotation or flip different from before), bake them
      // - If transforms are the same as before and already baked, DON'T rebake (preserve state)
      // - If transforms are the same but not baked, bake them now
      let hasBakedTransforms = false;

      if (isRotatedImage && rotationAngle !== undefined) {
        // Check if this is a NEW transform or just re-cropping with same transforms
        const transformsChanged = flipChanged || rotationChanged;

        if (transformsChanged) {
          // Transforms CHANGED - bake the new transforms
          finalRotation = 0;
          hasBakedTransforms = true;
        } else if (selectedImage.hasBakedTransforms) {
          // Transforms are the SAME and already baked - DON'T rebake
          // Keep using the already-baked image, just update crop area
          // Even if transforms were already baked earlier, the new cropped output
          // contains the transforms, so we must reset runtime rotation state.
          finalRotation = 0;
          hasBakedTransforms = true; // Keep it baked
        } else {
          // Transforms are the SAME but not yet baked - bake them now
          finalRotation = 0;
          hasBakedTransforms = true;
        }
      } else {
        // No transforms during crop, preserve existing rotation
        finalRotation = selectedImage.rotation || 0;
        hasBakedTransforms = selectedImage.hasBakedTransforms || false;
      }

      // Calculate the new aspect ratio based on the SCALED cropped dimensions
      const newAspectRatio = finalDisplayWidth / finalDisplayHeight;

      // CRITICAL: Manually create the updated images array to pass to saveToHistory
      // This ensures saveToHistory gets the correct state with cropData, even before React state updates
      let updatedImagesArray = null;

      setImages((prevImages) => {
        const updatedImages = prevImages.map((img) => {
          if (img.id === selectedImage.id) {
            // Create the updated image object with all crop data
            const updatedImage = {
              ...img,
              image: finalCroppedImage, // Use the appropriate cropped image (compressed or uncompressed)
              src: finalCroppedImageUrl, // Use the appropriate cropped image URL
              // CRITICAL: Set imageDataUrl to the cropped (and flipped if hasBakedTransforms) image URL
              // This ensures redo correctly restores the flipped cropped image
              imageDataUrl: finalCroppedImageUrl, // Use the same URL as src (cropped and flipped if hasBakedTransforms)
              // CRITICAL: Store the uncompressed cropped version immediately;
              // compressed bitmap will be generated asynchronously after the UI updates.
              compressedImage: null,
              uncompressedCroppedImage: uncompressedCroppedImage,
              uncompressedCroppedImageUrl: uncompressedCroppedImageUrl,
              // CRITICAL: Preserve the very first original uncropped image across multiple crops
              originalImage: selectedImage.originalImageDataUrl
                ? selectedImage.originalImage
                : trueOriginalImage,
              originalImageDataUrl: originalImageDataUrl,
              // Store crop data for both rotated and non-rotated images
              // For rotated images, the crop data represents the crop area in the rotated coordinate system
              // For non-rotated images, the crop data represents the crop area in the original coordinate system
              cropData: cropData,
              // Store the current rotation of the image for proper crop box restoration
              // This should be the rotation that was applied during the crop operation
              originalCropRotation: rotationAngle || 0,
              // Store flip transforms
              flipHorizontal: flipHorizontal || false,
              flipVertical: flipVertical || false,
              // Flag to indicate transforms are baked into the cropped image
              hasBakedTransforms: hasBakedTransforms,
              // Use the SCALED display dimensions (consistent with addImage behavior)
              width: finalDisplayWidth,
              height: finalDisplayHeight,
              // Set the new aspect ratio based on the SCALED cropped dimensions
              originalAspectRatio: newAspectRatio,
              // CRITICAL: Preserve the FIRST original display dimensions across multiple crops.
              // When isFullImageCrop we use finalDisplayWidth/Height (may be scaled if stored original was natural size) - store that as original so it stays correct.
              originalWidth: isFullImageCrop
                ? finalDisplayWidth
                : selectedImage.originalWidth ||
                (isAlreadyCropped
                  ? selectedImage.originalWidth || baselineWidth
                  : baselineWidth),
              originalHeight: isFullImageCrop
                ? finalDisplayHeight
                : selectedImage.originalHeight ||
                (isAlreadyCropped
                  ? selectedImage.originalHeight || baselineHeight
                  : baselineHeight),
              // CRITICAL FIX: Always set scale to 1 after crop
              // Flip transforms are handled via flipHorizontal/flipVertical flags in rendering
              scaleX: 1,
              scaleY: 1,
              // Set the final rotation (0 if baked, preserve if not)
              rotation: finalRotation,
              // Keep compression enabled
              compress: selectedImage.compress !== false ? true : false,
            };

            return updatedImage;
          }
          return img;
        });

        updatedImagesArray = updatedImages; // Capture for saveToHistory
        // CRITICAL: Set ref.main for main-canvas crop so "save from ref" uses main list only; clear offCanvas so we don't write main image there
        if (
          latestUpdatedImagesRef?.current &&
          selectedImage?.isOnTopLayer !== false
        ) {
          latestUpdatedImagesRef.current.main = updatedImages;
          latestUpdatedImagesRef.current.offCanvas = null;
        }
        return updatedImages;
      });

      // Also call updateImageProperties to ensure selectedObject is updated
      // CRITICAL: Pass skipHistorySave=true to prevent immediate history save
      // The crop changes will be saved when the element is unselected via element_edit_complete
      // This groups the crop operation with any other changes made during the editing session
      updateImageProperties(
        selectedImage.id,
        {
          image: finalCroppedImage,
          src: finalCroppedImageUrl,
          // CRITICAL: Set imageDataUrl to the cropped (and flipped if hasBakedTransforms) image URL
          // This ensures redo correctly restores the flipped cropped image
          imageDataUrl: finalCroppedImageUrl, // Use the same URL as src (cropped and flipped if hasBakedTransforms)
          compressedImage: null,
          uncompressedCroppedImage: uncompressedCroppedImage,
          uncompressedCroppedImageUrl: uncompressedCroppedImageUrl,
          originalImage: selectedImage.originalImageDataUrl
            ? selectedImage.originalImage
            : trueOriginalImage,
          originalImageDataUrl: originalImageDataUrl,
          cropData: cropData,
          originalCropRotation: rotationAngle || 0,
          flipHorizontal: flipHorizontal || false,
          flipVertical: flipVertical || false,
          hasBakedTransforms: hasBakedTransforms,
          width: finalDisplayWidth,
          height: finalDisplayHeight,
          originalAspectRatio: newAspectRatio,
          originalWidth: isFullImageCrop
            ? finalDisplayWidth
            : selectedImage.originalWidth || firstOriginalWidth,
          originalHeight: isFullImageCrop
            ? finalDisplayHeight
            : selectedImage.originalHeight || firstOriginalHeight,
          scaleX: 1,
          scaleY: 1,
          rotation: finalRotation,
          compress: selectedImage.compress !== false ? true : false,
          isCropOperation: true,
        },
        false, // isDragging=false
        true, // skipHistorySave=true - let element_edit_complete save when unselected
      );

      // CRITICAL: Set ref here with exact cropped dimensions so "save from ref" on unselect has correct width/height.
      // Only set ref.offCanvas when the image is on off-canvas; main-canvas crop already set ref.main above.
      if (latestUpdatedImagesRef && selectedImage?.isOnTopLayer === false) {
        const croppedImageForRef = {
          ...selectedImage,
          width: finalDisplayWidth,
          height: finalDisplayHeight,
          cropData,
          imageDataUrl: finalCroppedImageUrl,
          originalImageDataUrl,
          originalWidth: selectedImage.originalWidth || firstOriginalWidth,
          originalHeight: selectedImage.originalHeight || firstOriginalHeight,
          originalAspectRatio: newAspectRatio,
          originalCropRotation: rotationAngle || 0,
          flipHorizontal: flipHorizontal || false,
          flipVertical: flipVertical || false,
          hasBakedTransforms: hasBakedTransforms,
          scaleX: 1,
          scaleY: 1,
          rotation: finalRotation,
          compress: selectedImage.compress !== false ? true : false,
        };
        if (!latestUpdatedImagesRef.current) {
          latestUpdatedImagesRef.current = { main: null, offCanvas: null };
        }
        const currentOff = latestUpdatedImagesRef.current.offCanvas;
        const list =
          Array.isArray(currentOff) && currentOff.length > 0
            ? currentOff
            : [selectedImage];
        const nextOff = list.map((img) =>
          img.id === selectedImage.id ? croppedImageForRef : img,
        );
        latestUpdatedImagesRef.current.offCanvas = nextOff;
      }

      // PERFORMANCE: Kick off compression in the background so the user doesn't
      // wait on it when confirming the crop. When compression finishes, we only
      // update the bitmap data, preserving all geometry/crop state.
      if (selectedImage.compress !== false) {
        const schedule =
          typeof window.requestIdleCallback === "function"
            ? window.requestIdleCallback
            : (cb) => window.setTimeout(cb, 0);

        schedule(async () => {
          try {
            const compressed = await compressImage(uncompressedCroppedImage, 0.5);

            const canvas = document.createElement("canvas");
            canvas.width = compressed.width;
            canvas.height = compressed.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(compressed, 0, 0);
            const compressedUrl = canvas.toDataURL("image/jpeg", 0.5);

            // Update images with compressed bitmap; keep all other properties intact.
            setImages((prevImages) =>
              prevImages.map((img) => {
                if (img.id !== selectedImage.id) return img;
                const shouldUseCompressedForDisplay =
                  img.compress !== false && !!compressed;

                return {
                  ...img,
                  compressedImage: compressed,
                  ...(shouldUseCompressedForDisplay
                    ? {
                      image: compressed,
                      src: compressedUrl,
                      imageDataUrl: compressedUrl,
                    }
                    : {}),
                };
              }),
            );

            // Ensure selectedObject stays in sync (history still saved on unselect).
            const shouldUseCompressedForDisplay =
              selectedImage.compress !== false && !!compressed;
            updateImageProperties(
              selectedImage.id,
              {
                compressedImage: compressed,
                ...(shouldUseCompressedForDisplay
                  ? {
                    image: compressed,
                    src: compressedUrl,
                    imageDataUrl: compressedUrl,
                  }
                  : {}),
              },
              false,
              true, // skipHistorySave=true; crop history grouped by element_edit_complete
            );
          } catch (e) {
            // If compression fails, keep using the uncompressed bitmap.
          }
        });
      }
    } else {
    }
  };

  const addImageFromPreset = (preset) => {
    const img = new Image();
    img.src = preset.src;
    img.onload = async () => {
      // Compress the preset image by default
      const compressedImg = await compressImage(img);

      let updatedImagesArray = null;
      setImages((prev) => {
        const updated = [
          ...prev,
          {
            id: uuidv4(),
            image: compressedImg, // Use compressed image
            src: preset.src,
            width: preset.width,
            height: preset.height,
            x: 50,
            y: 50,
            maintainAspectRatio: preset.maintainAspectRatio,
            compress: true, // Always compress by default
            rotation: preset.rotation || 0,
            referenceUrl: preset.referenceUrl || "",
            originalImage: img, // Store original uncompressed
            compressedImage: compressedImg, // Store compressed version
            imageNumber: prev.length + 1,
            originalImageDataUrl: preset.src,
            imageDataUrl: preset.src,
            originalWidth: preset.width,
            originalHeight: preset.height,
            originalAspectRatio: preset.width / preset.height,
            isOnTopLayer: preset.isOnTopLayer !== false, // Default to true unless explicitly false
          },
        ];
        updatedImagesArray = updated;
        return updated;
      });

      // Save to history after adding preset image
      if (saveToHistory && updatedImagesArray) {
        setTimeout(() => {
          saveToHistory("add_preset_image", updatedImagesArray);
        }, 100);
      }
    };
  };

  const imageRotationValue = localInputValues.rotation;
  const hasImageRotationValue =
    imageRotationValue !== "" &&
    imageRotationValue !== null &&
    imageRotationValue !== undefined;
  const rotationInputValue = isEditingRotation
    ? hasImageRotationValue
      ? String(imageRotationValue)
      : ""
    : hasImageRotationValue
      ? `${imageRotationValue}°`
      : "0°";

  return (
    <div>
      {/* X and Y Position */}
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          padding: "10px 12px",
          marginBottom: "12px",
          display: "flex",
          gap: "16px",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <label style={{ whiteSpace: "nowrap", fontSize: "14px" }}>XPos</label>
          <input
            type="number"
            value={getDisplayValue(localInputValues.x)}
            step="any"
            min={MIN_X_CONSTRAINED}
            max={MAX_X_CONSTRAINED}
            onFocus={() => {
              isEditingXRef.current = true;
            }}
            onChange={(e) => {
              const { value } = e.target;
              const inputType = e.nativeEvent?.inputType;

              setLocalInputValues((prev) => ({
                ...prev,
                x: value,
              }));
              if (value === "" || shouldDeferInputUpdate(inputType)) {
                return;
              }
              commitPositionValue(
                "x",
                value,
                MIN_X_CONSTRAINED,
                MAX_X_CONSTRAINED,
              );
            }}
            onBlur={(e) => {
              isEditingXRef.current = false;
              commitPositionValue(
                "x",
                e.target.value,
                MIN_X_CONSTRAINED,
                MAX_X_CONSTRAINED,
              );
            }}
            onKeyDown={(e) =>
              {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  setTimeout(() => {
                    commitPositionValue(
                      "x",
                      e.currentTarget.value,
                      MIN_X_CONSTRAINED,
                      MAX_X_CONSTRAINED,
                    );
                  }, 0);
                  return;
                }
                handleEnterKey(e, () =>
                  commitPositionValue(
                    "x",
                    e.target.value,
                    MIN_X_CONSTRAINED,
                    MAX_X_CONSTRAINED,
                  ),
                );
              }
            }
            style={{
              width: "60px",
              padding: "6px 8px",
              marginLeft: "3px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <label style={{ whiteSpace: "nowrap", fontSize: "14px" }}>YPos</label>
          <input
            type="number"
            value={getDisplayValue(localInputValues.y)}
            step="any"
            min={MIN_Y_CONSTRAINED}
            max={MAX_Y_CONSTRAINED}
            onFocus={() => {
              isEditingYRef.current = true;
            }}
            onChange={(e) => {
              const { value } = e.target;
              const inputType = e.nativeEvent?.inputType;

              setLocalInputValues((prev) => ({
                ...prev,
                y: value,
              }));
              if (value === "" || shouldDeferInputUpdate(inputType)) {
                return;
              }
              commitPositionValue(
                "y",
                value,
                MIN_Y_CONSTRAINED,
                MAX_Y_CONSTRAINED,
              );
            }}
            onBlur={(e) => {
              isEditingYRef.current = false;
              commitPositionValue(
                "y",
                e.target.value,
                MIN_Y_CONSTRAINED,
                MAX_Y_CONSTRAINED,
              );
            }}
            onKeyDown={(e) =>
              {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  setTimeout(() => {
                    commitPositionValue(
                      "y",
                      e.currentTarget.value,
                      MIN_Y_CONSTRAINED,
                      MAX_Y_CONSTRAINED,
                    );
                  }, 0);
                  return;
                }
                handleEnterKey(e, () =>
                  commitPositionValue(
                    "y",
                    e.target.value,
                    MIN_Y_CONSTRAINED,
                    MAX_Y_CONSTRAINED,
                  ),
                );
              }
            }
            style={{
              width: "60px",
              padding: "6px 8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              marginLeft: "10px",
              fontSize: "14px",
            }}
          />
        </div>
      </div>

      {/* Width and Height */}
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          padding: "10px 12px",
          marginBottom: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label
            style={{ whiteSpace: "nowrap", fontSize: "14px", minWidth: "50px" }}
          >
            Width
          </label>
          <input
            type="number"
            step="0.10"
            value={localInputValues.width}
            min={MIN_WIDTH}
            max={maintainAspectRatio ? dynamicMaxWidth : maxWidthByStageFit}
            onFocus={() => {
              isEditingWidthRef.current = true;
            }}
            onChange={(e) => {
              const { value } = e.target;
              setLocalInputValues((prev) => ({
                ...prev,
                width: value,
              }));
            }}
            onBlur={(e) => {
              isEditingWidthRef.current = false;
              if (skipNextWidthBlurCommitRef.current) {
                skipNextWidthBlurCommitRef.current = false;
                return;
              }
              commitWidthValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                skipNextWidthBlurCommitRef.current = true;
                e.currentTarget.blur();
              }
              handleEnterKey(e, () => commitWidthValue(e.target.value));
            }}
            style={{
              flex: 1,
              padding: "6px 8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label
            style={{ whiteSpace: "nowrap", fontSize: "14px", minWidth: "50px" }}
          >
            Height
          </label>
          <input
            type="number"
            step="0.10"
            value={localInputValues.height}
            min={MIN_HEIGHT}
            max={
              maintainAspectRatio
                ? dynamicMaxHeightForInput
                : maxHeightByStageFitForInput
            }
            onFocus={() => {
              isEditingHeightRef.current = true;
            }}
            onChange={(e) => {
              const { value } = e.target;
              setLocalInputValues((prev) => ({
                ...prev,
                height: value,
              }));
            }}
            onBlur={(e) => {
              isEditingHeightRef.current = false;
              if (skipNextHeightBlurCommitRef.current) {
                skipNextHeightBlurCommitRef.current = false;
                return;
              }
              commitHeightValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                skipNextHeightBlurCommitRef.current = true;
                e.currentTarget.blur();
              }
              handleEnterKey(e, () => commitHeightValue(e.target.value));
            }}
            style={{
              flex: 1,
              padding: "6px 8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
        </div>
      </div>

      {/* Duplicate to Selected Location button */}
      {onToggleCloneMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            // Use setTimeout to ensure the toggle happens after event propagation is stopped
            setTimeout(() => {
              onToggleCloneMode();
            }, 0);
          }}
          style={{
            width: "100%",
            padding: "10px 20px",
            backgroundColor: isCloneMode ? "#3B82F6" : "#F3F4F6",
            color: isCloneMode ? "white" : "#374151",
            border: isCloneMode ? "1px solid #3B82F6" : "1px solid #D1D5DB",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            marginBottom: "12px",
          }}
        >
          <CopyIcon
            width={16}
            height={19}
            fill={isCloneMode ? "white" : "#374151"}
          />
          <span>Duplicate to Selected Location</span>
        </button>
      )}

      {/* Image Rotation */}
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          padding: "10px 12px",
          marginBottom: "12px",
          display: "flex",
          gap: "16px",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <label style={{ whiteSpace: "nowrap", fontSize: "14px" }}>
            Rotation
          </label>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input
              type="text"
              value={rotationInputValue}
              onChange={(e) => {
                const value = e.target.value;
                // Remove degree symbol and only allow numbers
                let numericValue = value.replace(/°/g, "");
                // Trim leading zeros (but keep one zero if the value is just zeros)
                if (numericValue.length > 1 && /^0+/.test(numericValue)) {
                  numericValue = numericValue.replace(/^0+/, "");
                }
                if (numericValue === "" || /^\d+$/.test(numericValue)) {
                  setLocalInputValues((prev) => ({
                    ...prev,
                    rotation: numericValue,
                  }));
                }
              }}
              onFocus={() => {
                setIsEditingRotation(true);
              }}
              onBlur={(e) => {
                setIsEditingRotation(false);
                const val = e.target.value.replace(/°/g, "");
                finalizeRotationChange(val);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const val = e.currentTarget.value.replace(/°/g, "");
                  finalizeRotationChange(val);
                  e.currentTarget.blur();
                }
              }}
              style={{
                width: "80px",
                padding: "6px 8px",
                border: "1px solid #ccc",
                borderRight: "none",
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                fontSize: "14px",
              }}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  startRotationChange(1);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  startRotationChange(1);
                }}
                onMouseUp={() => stopRotationChange(true)}
                onMouseLeave={() => stopRotationChange(true)}
                onTouchEnd={() => stopRotationChange(true)}
                onTouchCancel={() => stopRotationChange(true)}
                style={{
                  width: "20px",
                  height: "15px",
                  border: "1px solid #ccc",
                  borderTopRightRadius: "4px",
                  borderBottom: "none",
                  backgroundColor: "#f8f9fa",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  color: "#6C757D",
                }}
                title="Hold to increase rotation"
              >
                ▲
              </button>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  startRotationChange(-1);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  startRotationChange(-1);
                }}
                onMouseUp={() => stopRotationChange(true)}
                onMouseLeave={() => stopRotationChange(true)}
                onTouchEnd={() => stopRotationChange(true)}
                onTouchCancel={() => stopRotationChange(true)}
                style={{
                  width: "20px",
                  height: "15px",
                  border: "1px solid #ccc",
                  borderBottomRightRadius: "4px",
                  backgroundColor: "#f8f9fa",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  color: "#6C757D",
                }}
                title="Hold to decrease rotation"
              >
                ▼
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reference URL */}
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          padding: "10px 12px",
          marginBottom: "12px",
          position: "relative",
        }}
      >
        <label
          style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}
        >
          Reference URL
        </label>
        <input
          type="url"
          placeholder="https://"
          value={newReferenceUrl}
          onChange={handleReferenceUrlChange}
          onBlur={handleReferenceUrlEditComplete}
          style={{
            width: "100%",
            padding: "8px 36px 8px 8px", // extra right padding for icon
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxSizing: "border-box",
            fontSize: "14px",
          }}
        />
        <svg
          width="20"
          height="10"
          viewBox="0 0 20 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            position: "absolute",
            right: "16px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            marginTop: "13px",
          }}
          onClick={handleReferenceUrlClick}
          title="Apply Reference URL to Image"
        >
          <path
            d="M9 10H5C3.61667 10 2.4375 9.5125 1.4625 8.5375C0.4875 7.5625 0 6.38333 0 5C0 3.61667 0.4875 2.4375 1.4625 1.4625C2.4375 0.4875 3.61667 0 5 0H9V2H5C4.16667 2 3.45833 2.29167 2.875 2.875C2.29167 3.45833 2 4.16667 2 5C2 5.83333 2.29167 6.54167 2.875 7.125C3.45833 7.70833 4.16667 8 5 8H9V10ZM6 6V4H14V6H6ZM11 10V8H15C15.8333 8 16.5417 7.70833 17.125 7.125C17.7083 6.54167 18 5.83333 18 5C18 4.16667 17.7083 3.45833 17.125 2.875C16.5417 2.29167 15.8333 2 15 2H11V0H15C16.3833 0 17.5625 0.4875 18.5375 1.4625C19.5125 2.4375 20 3.61667 20 5C20 6.38333 19.5125 7.5625 18.5375 8.5375C17.5625 9.5125 16.3833 10 15 10H11Z"
            fill="#0D6EFD"
          />
        </svg>
      </div>

      {/* Image Preset Controls */}
      <div
        style={{
          background: "#f7f7f7",
          borderRadius: "5px",
          marginTop: "1rem",
          padding: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            marginBottom: "12px",
          }}
          onClick={() => setIsImagePresetsExpanded(!isImagePresetsExpanded)}
        >
          <h4 style={{ margin: "0", fontWeight: "normal", fontSize: "16px" }}>
            Styles
          </h4>
          <svg
            width="12"
            height="7"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: isImagePresetsExpanded
                ? "rotate(0deg)"
                : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
              fill="#6C757D"
            />
          </svg>
        </div>

        {isImagePresetsExpanded && (
          <>
            {imagePresets.map((preset) => (
              <div key={preset.id}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0",
                    borderBottom: "1px solid #eee",
                  }}
                >
                <button
                  type="button"
                  onClick={() => {
                    if (
                      editingImagePreset &&
                      editingImagePreset.id !== preset.id
                    )
                      return;
                    applyImagePreset(preset);
                  }}
                  disabled={
                    Boolean(editingImagePreset) &&
                    editingImagePreset.id !== preset.id
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor:
                      editingImagePreset &&
                      editingImagePreset.id !== preset.id
                        ? "not-allowed"
                        : "pointer",
                    flexGrow: 1,
                    background: "none",
                    border: "none",
                    padding: 0,
                    textAlign: "left",
                    font: "inherit",
                    color:
                      editingImagePreset &&
                      editingImagePreset.id !== preset.id
                        ? "#9ca3af"
                        : "inherit",
                    opacity:
                      editingImagePreset &&
                      editingImagePreset.id !== preset.id
                        ? 0.55
                        : 1,
                  }}
                >
                  <span style={{ fontSize: "14px" }}>{preset.name}</span>
                </button>

                <div style={{ display: "flex", gap: "10px" }}>
                  {/* <button
                    type="button"
                    onClick={() => {
                      if (
                        editingImagePreset &&
                        editingImagePreset.id !== preset.id
                      )
                        return;
                      const toSave =
                        editingImagePreset &&
                        editingImagePreset.id === preset.id
                          ? editingImagePreset
                          : preset;
                      saveDefaultImagePresetToStorage(toSave);
                    }}
                    disabled={
                      Boolean(editingImagePreset) &&
                      editingImagePreset.id !== preset.id
                    }
                    title="Set as default"
                    style={{
                      backgroundColor:
                        String(preset.id) === String(defaultImagePresetId)
                          ? "#2563eb"
                          : "#e5e7eb",
                      color:
                        String(preset.id) === String(defaultImagePresetId)
                          ? "white"
                          : "#111827",
                      border: "none",
                      borderRadius: "4px",
                      cursor:
                        editingImagePreset &&
                        editingImagePreset.id !== preset.id
                          ? "not-allowed"
                          : "pointer",
                      padding: "2px 6px",
                      fontSize: "11px",
                      opacity:
                        editingImagePreset &&
                        editingImagePreset.id !== preset.id
                          ? 0.45
                          : 1,
                    }}
                  >
                    Default
                  </button> */}
                  <button
                    type="button"
                    onClick={() =>
                      !editingImagePreset && setEditingImagePreset(preset)
                    }
                    disabled={Boolean(editingImagePreset)}
                    title="Edit"
                    style={{
                      backgroundColor: "#007bff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: editingImagePreset ? "not-allowed" : "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "22px",
                      height: "22px",
                      opacity: editingImagePreset ? 0.45 : 1,
                    }}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_15909_16836)">
                        <path
                          d="M11.6263 1.45481C11.6964 1.5251 11.7357 1.6203 11.7357 1.71956C11.7357 1.81881 11.6964 1.91401 11.6263 1.98431L10.844 2.76731L9.34404 1.26731L10.1263 0.484307C10.1966 0.414005 10.292 0.374512 10.3914 0.374512C10.4908 0.374512 10.5862 0.414005 10.6565 0.484307L11.6263 1.45406V1.45481ZM10.3138 3.29681L8.81379 1.79681L3.70404 6.90731C3.66276 6.94858 3.63168 6.99891 3.61329 7.05431L3.00954 8.86481C2.99859 8.89781 2.99703 8.93321 3.00505 8.96705C3.01306 9.00089 3.03033 9.03184 3.05492 9.05643C3.07951 9.08102 3.11045 9.09828 3.14429 9.1063C3.17813 9.11431 3.21353 9.11276 3.24654 9.10181L5.05704 8.49806C5.11236 8.47988 5.16269 8.44906 5.20404 8.40806L10.3138 3.29681Z"
                          fill="white"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M0.75 10.125C0.75 10.4234 0.868526 10.7095 1.0795 10.9205C1.29048 11.1315 1.57663 11.25 1.875 11.25H10.125C10.4234 11.25 10.7095 11.1315 10.9205 10.9205C11.1315 10.7095 11.25 10.4234 11.25 10.125V5.625C11.25 5.52554 11.2105 5.43016 11.1402 5.35984C11.0698 5.28951 10.9745 5.25 10.875 5.25C10.7755 5.25 10.6802 5.28951 10.6098 5.35984C10.5395 5.43016 10.5 5.52554 10.5 5.625V10.125C10.5 10.2245 10.4605 10.3198 10.3902 10.3902C10.3198 10.4605 10.2245 10.5 10.125 10.5H1.875C1.77554 10.5 1.68016 10.4605 1.60984 10.3902C1.53951 10.3198 1.5 10.2245 1.5 10.125V1.875C1.5 1.77554 1.53951 1.68016 1.60984 1.60984C1.68016 1.53951 1.77554 1.5 1.875 1.5H6.75C6.84946 1.5 6.94484 1.46049 7.01517 1.39017C7.08549 1.31984 7.125 1.22446 7.125 1.125C7.125 1.02554 7.08549 0.930161 7.01517 0.859835C6.94484 0.789509 6.84946 0.75 6.75 0.75H1.875C1.57663 0.75 1.29048 0.868526 1.0795 1.0795C0.868526 1.29048 0.75 1.57663 0.75 1.875V10.125Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_15909_16836">
                          <rect width="12" height="12" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (editingImagePreset) return;
                      const filtered = imagePresets.filter(
                        (p) => p.id !== preset.id,
                      );
                      setImagePresets(filtered);
                      saveImagePresetsToStorage(filtered);
                      if (String(defaultImagePresetId) === String(preset.id)) {
                        localStorage.removeItem(
                          IMAGE_DEFAULT_PRESET_STORAGE_KEY,
                        );
                        localStorage.removeItem(
                          IMAGE_DEFAULT_PRESET_ID_STORAGE_KEY,
                        );
                        setDefaultImagePresetId(null);
                      }
                      // Clear editing state if the deleted preset is being edited
                      if (
                        editingImagePreset &&
                        editingImagePreset.id === preset.id
                      ) {
                        setEditingImagePreset(null);
                      }
                    }}
                    disabled={Boolean(editingImagePreset)}
                    title="Delete"
                    style={{
                      backgroundColor: "#dc3545",
                      border: "none",
                      borderRadius: "4px",
                      cursor: editingImagePreset ? "not-allowed" : "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "22px",
                      height: "22px",
                      opacity: editingImagePreset ? 0.45 : 1,
                    }}
                  >
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="18" height="18" rx="4" fill="#DC3545" />
                      <path
                        d="M5.875 14.5C5.53125 14.5 5.23698 14.3803 4.99219 14.141C4.7474 13.9016 4.625 13.6139 4.625 13.2778V5.33333H4V4.11111H7.125V3.5H10.875V4.11111H14V5.33333H13.375V13.2778C13.375 13.6139 13.2526 13.9016 13.0078 14.141C12.763 14.3803 12.4688 14.5 12.125 14.5H5.875ZM7.125 12.0556H8.375V6.55556H7.125V12.0556ZM9.625 12.0556H10.875V6.55556H9.625V12.0556Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </div>
                </div>
                {editingImagePreset && editingImagePreset.id === preset.id && (
                  <div
                    style={{
                      marginTop: "8px",
                      marginBottom: "12px",
                      padding: "12px",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                    }}
                  >
                    <h4>Edit Image Preset</h4>
                    <input
                      type="text"
                      value={editingImagePreset.name}
                      onChange={(e) =>
                        setEditingImagePreset({
                          ...editingImagePreset,
                          name: e.target.value,
                        })
                      }
                      placeholder="Preset name"
                      style={{
                        width: "100%",
                        padding: "6px",
                        border: "1px solid #ccc",
                        marginBottom: "12px",
                      }}
                    />
                    <button
                      onClick={() => {
                        const updated = imagePresets.map((p) =>
                          p.id === editingImagePreset.id ? editingImagePreset : p,
                        );
                        setImagePresets(updated);
                        saveImagePresetsToStorage(updated);
                        setEditingImagePreset(null);
                        setPendingNewImagePresetId(null);
                      }}
                      style={{
                        backgroundColor: "#2563eb",
                        color: "white",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        border: "none",
                        cursor: "pointer",
                        marginRight: "8px",
                      }}
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => {
                        if (pendingNewImagePresetId) {
                          const rolledBack = imagePresets.filter(
                            (p) => p.id !== pendingNewImagePresetId,
                          );
                          setImagePresets(rolledBack);
                        }
                        setEditingImagePreset(null);
                        setPendingNewImagePresetId(null);
                      }}
                      style={{
                        backgroundColor: "#e5e7eb",
                        color: "#111827",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={() => {
                if (editingImagePreset) return;
                if (selectedImage && selectedImage.image) {
                  const existingNumbers = imagePresets
                    .map((img) => {
                      const match = img.name.match(/^Picture (\d+)$/);
                      return match ? parseInt(match[1], 10) : null;
                    })
                    .filter((num) => num !== null);

                  // Find the smallest unused number
                  let imageNumber = 1;
                  while (existingNumbers.includes(imageNumber)) {
                    imageNumber++;
                  }

                  const base64Src = convertImageToBase64(selectedImage.image); // ✅ Convert to Base64

                  // Use current input value (newReferenceUrl) so preset gets the URL even if user hasn't blurred the field yet
                  const refUrlForPreset =
                    (newReferenceUrl && newReferenceUrl.trim()) ||
                    selectedImage.referenceUrl ||
                    "";
                  const newPreset = {
                    id: uuidv4(),
                    name: `Picture ${imageNumber}`, // ✅ Unique name
                    src: base64Src,
                    width: selectedImage.width,
                    height: selectedImage.height,
                    maintainAspectRatio: selectedImage.maintainAspectRatio,
                    compress: selectedImage.compress,
                    x: selectedImage.x,
                    y: selectedImage.y,
                    rotation: selectedImage.rotation || 0,
                    referenceUrl: refUrlForPreset,
                    scaleX: selectedImage.scaleX || 1,
                    scaleY: selectedImage.scaleY || 1,
                    cropData: selectedImage.cropData,
                    originalAspectRatio: selectedImage.originalAspectRatio,
                    originalWidth: selectedImage.originalWidth,
                    originalHeight: selectedImage.originalHeight,
                    isOnTopLayer: selectedImage.isOnTopLayer !== false, // Default to true unless explicitly false
                  };

                  const updatedPresets = [...imagePresets, newPreset];
                  setImagePresets(updatedPresets);

                  // Immediately show the edit box for the newly created preset
                  setEditingImagePreset(newPreset);
                  setPendingNewImagePresetId(newPreset.id);
                }
              }}
              disabled={Boolean(editingImagePreset)}
              style={{
                background: "#f7f7f7",
                border: "none",
                width: "100%",
                color: "#007bff",
                fontSize: "13px",
                marginTop: "8px",
                opacity: editingImagePreset ? 0.5 : 1,
                cursor: editingImagePreset ? "not-allowed" : "pointer",
              }}
            >
              <span
                style={{
                  fontSize: "16px",
                  marginRight: "8px",
                }}
              >
                +
              </span>
              Add this picture as a Style
            </button>
          </>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px", // spacing between groups
          marginTop: "16px",
        }}
      >
        {/* Group 1 */}
        <div style={groupStyle}>
          <button
            onClick={() => rotate(-90)}
            title="Rotate Left"
            className="icon-btn"
          >
            <svg
              width="19"
              height="19"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.3431 20C10.5551 20 9.78258 19.8924 9.02543 19.6772C8.26846 19.4622 7.5557 19.1459 6.88714 18.7284L7.97689 17.6386C8.49605 17.9233 9.04034 18.1369 9.60975 18.2792C10.1792 18.4216 10.7569 18.4927 11.3431 18.4927C13.3025 18.4927 14.9647 17.8103 16.3296 16.4454C17.6945 15.0805 18.3769 13.4183 18.3769 11.4589C18.3769 9.49947 17.6945 7.8373 16.3296 6.4724C14.9647 5.1075 13.3025 4.42505 11.3431 4.42505H10.6126L12.4019 6.21441L11.3431 7.34284L7.67167 3.67142L11.3431 0L12.4019 1.12843L10.6126 2.91779H11.3431C13.7155 2.91779 15.7321 3.74804 17.393 5.40853C19.0538 7.06902 19.8842 9.08539 19.8842 11.4576C19.8842 12.6437 19.66 13.7545 19.2117 14.7902C18.7634 15.826 18.154 16.7291 17.3837 17.4995C16.6133 18.2698 15.7104 18.8792 14.6749 19.3275C13.6394 19.7758 12.5288 20 11.3431 20ZM5.31407 16.773L0 11.4589L5.31407 6.14482L10.6281 11.4589L5.31407 16.773ZM5.31407 14.6241L8.47931 11.4589L5.31407 8.29366L2.14884 11.4589L5.31407 14.6241Z"
                fill="#6C757D"
              />
            </svg>
          </button>
          <button
            onClick={() => rotate(90)}
            title="Rotate Right"
            className="icon-btn"
          >
            <svg
              width="20"
              height="19"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.99032 20C7.80462 20 6.69402 19.7758 5.65854 19.3275C4.62305 18.8792 3.72013 18.2698 2.94975 17.4995C2.17938 16.7291 1.57003 15.8262 1.1217 14.7909C0.673381 13.7556 0.449219 12.6452 0.449219 11.4597C0.449219 9.08874 1.27963 7.07237 2.94046 5.41054C4.60128 3.74871 6.61791 2.91779 8.99032 2.91779H9.72084L7.93148 1.12843L8.99032 0L12.6617 3.67142L8.99032 7.34284L7.93148 6.21441L9.72084 4.42505H8.99032C7.03089 4.42505 5.36873 5.1075 4.00383 6.4724C2.63892 7.8373 1.95647 9.49947 1.95647 11.4589C1.95647 13.4183 2.63892 15.0805 4.00383 16.4454C5.36873 17.8103 7.03089 18.4927 8.99032 18.4927C9.57648 18.4927 10.1543 18.4216 10.7237 18.2792C11.2931 18.1369 11.8374 17.9233 12.3565 17.6386L13.4463 18.7284C12.7777 19.1459 12.065 19.4622 11.308 19.6772C10.5508 19.8924 9.77828 20 8.99032 20ZM15.0193 16.773L9.70526 11.4589L15.0193 6.14482L20.3334 11.4589L15.0193 16.773ZM15.0193 14.6241L18.1846 11.4589L15.0193 8.29366L11.8541 11.4589L15.0193 14.6241Z"
                fill="#6C757D"
              />
            </svg>
          </button>
        </div>

        {/* Group 2 */}
        <div style={groupStyle}>
          <button
            onClick={handleCropClick}
            title={selectedImage.cropData ? "Edit Crop" : "Crop Image"}
            className="icon-btn"
            disabled={isCropLoading}
            style={{
              opacity: isCropLoading ? 0.6 : 1,
              cursor: isCropLoading ? "wait" : "pointer",
            }}
          >
            {isCropLoading ? (
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid #6C757D",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            ) : (
              <svg
                width="20"
                height="19"
                viewBox="0 0 21 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.2158 20V16.2791H5.73459C5.26467 16.2791 4.86692 16.1163 4.54133 15.7907C4.21575 15.4651 4.05296 15.0674 4.05296 14.5974V5.11628H0.332031V3.72093H4.05296V0H5.44831V14.5974C5.44831 14.6691 5.47816 14.7347 5.53785 14.7942C5.59738 14.8539 5.66296 14.8837 5.73459 14.8837H20.332V16.2791H16.6111V20H15.2158ZM15.2158 13.4884V5.40256C15.2158 5.33093 15.1859 5.26535 15.1262 5.20581C15.0667 5.14612 15.0011 5.11628 14.9295 5.11628H6.84366V3.72093H14.9295C15.3994 3.72093 15.7971 3.88372 16.1227 4.2093C16.4483 4.53488 16.6111 4.93264 16.6111 5.40256V13.4884H15.2158Z"
                  fill="#6C757D"
                />
              </svg>
            )}
          </button>
          <button
            onClick={triggerReplace}
            title="Replace Image"
            className="icon-btn"
            disabled={isReplacingImage}
            style={{
              opacity: isReplacingImage ? 0.6 : 1,
              cursor: isReplacingImage ? "wait" : "pointer",
            }}
          >
            {isReplacingImage ? (
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid #6C757D",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            ) : (
              <svg
                width="17"
                height="19"
                viewBox="0 0 18 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.80414 17.1661H11.6341L9.71703 14.6579L8.30203 16.3318L7.35677 15.075L5.80414 17.1661ZM4.83256 20C4.30765 20 3.85922 19.8141 3.48729 19.4424C3.11554 19.0704 2.92966 18.622 2.92966 18.0971V10.3239C2.92966 9.79904 3.11554 9.35061 3.48729 8.97868C3.85922 8.60693 4.30765 8.42105 4.83256 8.42105H12.6057C13.1306 8.42105 13.579 8.60693 13.951 8.97868C14.3227 9.35061 14.5086 9.79904 14.5086 10.3239V18.0971C14.5086 18.622 14.3227 19.0704 13.951 19.4424C13.579 19.8141 13.1306 20 12.6057 20H4.83256ZM4.83256 18.4211H12.6057C12.7003 18.4211 12.7779 18.3907 12.8386 18.33C12.8993 18.2693 12.9297 18.1917 12.9297 18.0971V10.3239C12.9297 10.2294 12.8993 10.1518 12.8386 10.0911C12.7779 10.0304 12.7003 10 12.6057 10H4.83256C4.738 10 4.66036 10.0304 4.59966 10.0911C4.53896 10.1518 4.50861 10.2294 4.50861 10.3239V18.0971C4.50861 18.1917 4.53896 18.2693 4.59966 18.33C4.66036 18.3907 4.738 18.4211 4.83256 18.4211ZM0.207031 6.19447C0.799487 4.33605 1.87879 2.83903 3.44493 1.70342C5.01107 0.567807 6.76914 0 8.71914 0C10.2184 0 11.6196 0.354299 12.9226 1.0629C14.2255 1.77132 15.2805 2.74772 16.0876 3.99211V0.526316H17.6665V6.19447H11.9986V4.61553H14.5047C13.8676 3.66816 13.036 2.92518 12.0097 2.38658C10.9833 1.84816 9.88651 1.57895 8.71914 1.57895C7.21037 1.57895 5.8365 1.99667 4.59756 2.83211C3.35879 3.66737 2.46273 4.78816 1.9094 6.19447H0.207031Z"
                  fill="#6C757D"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Group 3 */}
        <div style={groupStyle}>
          <button onClick={onCopy} title="Copy" className="icon-btn">
            <svg
              width="16"
              height="19"
              viewBox="0 0 17 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.97572 15.75C5.47055 15.75 5.04297 15.575 4.69297 15.225C4.34297 14.875 4.16797 14.4474 4.16797 13.9423V2.55775C4.16797 2.05258 4.34297 1.625 4.69297 1.275C5.04297 0.925 5.47055 0.75 5.97572 0.75H14.3602C14.8654 0.75 15.293 0.925 15.643 1.275C15.993 1.625 16.168 2.05258 16.168 2.55775V13.9423C16.168 14.4474 15.993 14.875 15.643 15.225C15.293 15.575 14.8654 15.75 14.3602 15.75H5.97572ZM5.97572 14.25H14.3602C14.4372 14.25 14.5077 14.2179 14.5717 14.1538C14.6359 14.0898 14.668 14.0192 14.668 13.9423V2.55775C14.668 2.48075 14.6359 2.41025 14.5717 2.34625C14.5077 2.28208 14.4372 2.25 14.3602 2.25H5.97572C5.89872 2.25 5.82822 2.28208 5.76422 2.34625C5.70005 2.41025 5.66797 2.48075 5.66797 2.55775V13.9423C5.66797 14.0192 5.70005 14.0898 5.76422 14.1538C5.82822 14.2179 5.89872 14.25 5.97572 14.25ZM2.47572 19.25C1.97055 19.25 1.54297 19.075 1.19297 18.725C0.842969 18.375 0.667969 17.9474 0.667969 17.4423V4.55775H2.16797V17.4423C2.16797 17.5193 2.20005 17.5898 2.26422 17.6538C2.32822 17.7179 2.39872 17.75 2.47572 17.75H12.3602V19.25H2.47572Z"
                fill="#6C757D"
              />
            </svg>
          </button>
          <button onClick={onCut} title="Cut" className="icon-btn">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.577 18.7692L10.4233 11.6152L7.68853 14.3499C7.82836 14.5999 7.92803 14.865 7.98753 15.1452C8.0472 15.4254 8.07703 15.7103 8.07703 15.9999C8.07703 17.0048 7.71936 17.8649 7.00403 18.5804C6.2887 19.2959 5.42878 19.6537 4.42428 19.6537C3.41978 19.6537 2.55953 19.296 1.84353 18.5807C1.12753 17.8654 0.769531 17.0054 0.769531 16.0009C0.769531 14.9964 1.12728 14.1362 1.84278 13.4202C2.55828 12.7042 3.41845 12.3462 4.42328 12.3462C4.71295 12.3462 4.99787 12.3759 5.27803 12.4354C5.5582 12.4951 5.82328 12.5949 6.07328 12.7347L8.80778 9.99994L6.07328 7.26519C5.82328 7.40503 5.5582 7.50478 5.27803 7.56444C4.99787 7.62394 4.71295 7.65369 4.42328 7.65369C3.41845 7.65369 2.55828 7.29603 1.84278 6.58069C1.12728 5.86536 0.769531 5.00544 0.769531 4.00094C0.769531 2.99644 1.1272 2.13619 1.84253 1.42019C2.55786 0.704192 3.41778 0.346191 4.42228 0.346191C5.42678 0.346191 6.28703 0.703943 7.00303 1.41944C7.71903 2.13494 8.07703 2.99511 8.07703 3.99994C8.07703 4.28961 8.0472 4.57453 7.98753 4.85469C7.92803 5.13486 7.82836 5.39994 7.68853 5.64994L20 17.9614V18.7692H17.577ZM13.1925 8.84619L11.577 7.23069L17.577 1.23069H20V2.03844L13.1925 8.84619ZM4.42328 6.15369C5.01561 6.15369 5.5227 5.94286 5.94453 5.52119C6.3662 5.09936 6.57703 4.59228 6.57703 3.99994C6.57703 3.40761 6.3662 2.90053 5.94453 2.47869C5.5227 2.05703 5.01561 1.84619 4.42328 1.84619C3.83095 1.84619 3.32386 2.05703 2.90203 2.47869C2.4802 2.90053 2.26928 3.40761 2.26928 3.99994C2.26928 4.59228 2.4802 5.09936 2.90203 5.52119C3.32386 5.94286 3.83095 6.15369 4.42328 6.15369ZM10.4233 10.2499C10.4899 10.2499 10.5483 10.2249 10.5983 10.1749C10.6483 10.1249 10.6733 10.0666 10.6733 9.99994C10.6733 9.93328 10.6483 9.87494 10.5983 9.82494C10.5483 9.77494 10.4899 9.74994 10.4233 9.74994C10.3566 9.74994 10.2983 9.77494 10.2483 9.82494C10.1983 9.87494 10.1733 9.93328 10.1733 9.99994C10.1733 10.0666 10.1983 10.1249 10.2483 10.1749C10.2983 10.2249 10.3566 10.2499 10.4233 10.2499ZM4.42328 18.1537C5.01561 18.1537 5.5227 17.9429 5.94453 17.5212C6.3662 17.0994 6.57703 16.5923 6.57703 15.9999C6.57703 15.4076 6.3662 14.9005 5.94453 14.4787C5.5227 14.057 5.01561 13.8462 4.42328 13.8462C3.83095 13.8462 3.32386 14.057 2.90203 14.4787C2.4802 14.9005 2.26928 15.4076 2.26928 15.9999C2.26928 16.5923 2.4802 17.0994 2.90203 17.5212C3.32386 17.9429 3.83095 18.1537 4.42328 18.1537Z"
                fill="#6C757D"
              />
            </svg>
          </button>
        </div>
      </div>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleReplaceImage}
      />

      {/* Compression Checkbox */}
      <div style={{ marginTop: "16px" }}>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "14px",
          }}
        >
          <input
            type="checkbox"
            checked={selectedImage?.compress !== false} // Default to true if not explicitly set to false
            onChange={(e) => {
              const checked = e.target.checked;

              // Call the toggle function with the image ID and new state
              toggleImageCompression(selectedImage.id, checked);
            }}
            style={{ marginRight: "8px" }}
          />
          Allow Image Compression
        </label>
      </div>

      {/* Maintain Aspect Ratio Checkbox */}
      <div style={{ marginTop: "8px" }}>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "14px",
          }}
        >
          <input
            type="checkbox"
            checked={maintainAspectRatio}
            onChange={(e) => {
              const checked = e.target.checked;

              if (checked && selectedImage?.width && selectedImage?.height) {
                // When re-enabling "Maintain aspect ratio", correct current dimensions to match the
                // true content aspect ratio. Do NOT restore to originalWidth/originalHeight because
                // those may be missing or overwritten (e.g. to stage size after a stretch), which
                // would make the image fill the entire canvas instead of keeping its proper ratio.
                const aspectRatio =
                  selectedImage.originalAspectRatio ||
                  selectedImage.width / selectedImage.height;
                let newWidth = selectedImage.width;
                let newHeight = selectedImage.height;
                // Correct dimensions to match aspect ratio (shrink the dimension that is too large)
                if (aspectRatio > newWidth / newHeight) {
                  newHeight = newWidth / aspectRatio;
                } else {
                  newWidth = newHeight * aspectRatio;
                }
                newWidth = Math.max(
                  MIN_WIDTH,
                  Math.min(dynamicMaxWidth, newWidth),
                );
                newHeight = Math.max(
                  MIN_HEIGHT,
                  Math.min(dynamicMaxHeight, newHeight),
                );

                const updatedImage = {
                  ...selectedImage,
                  maintainAspectRatio: true,
                  width: newWidth,
                  height: newHeight,
                  originalAspectRatio: aspectRatio,
                  originalWidth: newWidth,
                  originalHeight: newHeight,
                };

                // CRITICAL: Pass skipHistorySave=true to prevent saving to history on each checkbox change
                // History will be saved when element is unselected via element_edit_complete
                // This groups all property changes (including maintainAspectRatio) into a single history entry
                // Similar to how rotation changes are handled
                updateImageProperties(
                  selectedImage.id,
                  {
                    maintainAspectRatio: true,
                    width: newWidth,
                    height: newHeight,
                    originalAspectRatio: aspectRatio,
                    originalWidth: newWidth,
                    originalHeight: newHeight,
                  },
                  false,
                  true,
                ); // isDragging=false, skipHistorySave=true

                // Update local input values to reflect the corrected dimensions
                setLocalInputValues((prev) => ({
                  ...prev,
                  width: newWidth.toFixed(2),
                  height: newHeight.toFixed(2),
                }));

                // Update selected object and selected objects
                if (setSelectedObject) {
                  setSelectedObject(updatedImage);
                }
                if (setSelectedObjects) {
                  setSelectedObjects([updatedImage]);
                }
              } else {
                const updatedImage = {
                  ...selectedImage,
                  maintainAspectRatio: false,
                };

                // CRITICAL: Pass skipHistorySave=true to prevent saving to history on each checkbox change
                // History will be saved when element is unselected via element_edit_complete
                // This groups all property changes (including maintainAspectRatio) into a single history entry
                // Similar to how rotation changes are handled
                updateImageProperties(
                  selectedImage.id,
                  {
                    maintainAspectRatio: false,
                  },
                  false,
                  true,
                ); // isDragging=false, skipHistorySave=true

                // Update selected object and selected objects
                if (setSelectedObject) {
                  setSelectedObject(updatedImage);
                }
                if (setSelectedObjects) {
                  setSelectedObjects([updatedImage]);
                }
              }
            }}
            style={{ marginRight: "8px" }}
          />
          Maintain Aspect Ratio
        </label>
      </div>

      {/* Display Above Elements */}
      <div style={{ marginTop: "8px" }}>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "14px",
          }}
        >
          <input
            type="checkbox"
            checked={isOnTopLayer}
            onChange={(e) => {
              const checked = e.target.checked;

              // CRITICAL: Pass skipHistorySave=true to prevent immediate history save
              // This ensures the change is grouped with other property changes
              // and saved as ONE history entry when the element is unselected
              // This matches the behavior of rectangles
              updateImageProperties(
                selectedImage.id,
                {
                  isOnTopLayer: checked,
                },
                false, // isDragging=false
                true, // skipHistorySave=true (will set pendingChanges for endElementEditing)
              );

              // DO NOT call saveToHistory here - let endElementEditing handle it
              // This ensures all property changes are grouped into one history entry
            }}
            style={{ marginRight: "8px" }}
          />
          Display on Top
        </label>
      </div>

      {/* Delete Button */}
      <div style={{ marginTop: "24px" }}>
        <button
          onClick={onDelete}
          style={{
            width: "100%",
            height: "40px",
            backgroundColor: "white",
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "2px solid red",
            color: "red",
            marginTop: "10px",
          }}
        >
          Delete Picture
        </button>
      </div>

      {/* Advanced Crop Modal */}
      { }
      <AdvancedCropModal
        isOpen={isCropModalOpen}
        onClose={handleCropModalClose}
        onModalRootRef={setCropModalRootRef}
        imageSrc={
          // CRITICAL: Always show the original uncropped image in crop modal
          // This ensures that when cropping again, we crop from the original source, not the already-cropped image
          (() => {
            // First priority: use the stored original image data URL (the very first original)
            if (selectedImage?.originalImageDataUrl) {
              return selectedImage.originalImageDataUrl;
            }
            // Second priority: convert original image element to data URL
            if (selectedImage?.originalImage instanceof HTMLImageElement) {
              return imageToDataURL(selectedImage.originalImage);
            }
            // Third priority: use the current image (only for first-time crop)
            if (selectedImage?.image instanceof HTMLImageElement) {
              return imageToDataURL(selectedImage.image);
            }
            // Fallback to src
            return selectedImage?.src;
          })()
        }
        imageId={selectedImage?.id}
        onCropConfirm={handleCropConfirm}
        currentRotation={selectedImage?.originalCropRotation || 0}
        currentFlipHorizontal={selectedImage?.flipHorizontal || false}
        currentFlipVertical={selectedImage?.flipVertical || false}
        currentCropData={selectedImage?.cropData || null}
        originalImageData={
          // Always pass the original image data for cropping operations
          selectedImage?.originalImage || selectedImage?.image
        }
        currentDisplayWidth={
          // Use original display dimensions if available (before any cropping)
          // This prevents the modal from getting smaller on successive crops
          selectedImage?.originalWidth || selectedImage?.width
        }
        currentDisplayHeight={
          selectedImage?.originalHeight || selectedImage?.height
        }
      />

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

ImageEditor.propTypes = {
  selectedImage: PropTypes.object.isRequired,
  updateImageProperties: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onCopy: PropTypes.func,
  onCut: PropTypes.func,
  toggleImageCompression: PropTypes.func,
  isCropModalOpen: PropTypes.bool,
  setIsCropModalOpen: PropTypes.func,
  setCropModalOpenRefOnly: PropTypes.func,
  setCropModalRootRef: PropTypes.func,
};

export default ImageEditor;
